import React, { useRef, useCallback, useLayoutEffect, useState } from 'react';
import { SectionData, DiscoveredBlock } from '../../hooks/usePdfExportStateV2';
import { SuiteSection, isSuiteSectionType } from './SuiteSections';

// ---------------------------------------------------------------------------
// Granular sub-block removal
//
// Sections render their inner colored cards (e.g. "Top Concerns", "Financial
// Summary") as plain JSX with no ids. Rather than hand-tag hundreds of blocks
// across ~18 report types, RemovableBlocksLayer discovers them generically from
// the rendered DOM by their styling + heading, tags each with a stable
// data-block-id, hides removed ones, and reports the list up for the sidebar.
// It runs inside EVERY SectionRenderer instance (the hidden measurement
// container AND each printed page), so removed blocks shrink measured heights
// (correct pagination) and disappear from the printed output.
// ---------------------------------------------------------------------------
const BLOCK_COLORS = 'blue|green|red|indigo|amber|emerald|purple|orange|yellow|teal|cyan|rose|pink|sky|violet|lime|fuchsia';
const RE_BLOCK_BG = new RegExp(`(?:^|\\s)bg-(?:${BLOCK_COLORS})-(?:50|100)(?:\\s|$)`);
const RE_BLOCK_BAR = /border-l-4/;
const RE_BLOCK_BORDER = new RegExp(`border-(?:${BLOCK_COLORS})-(?:200|300|400|500|600)`);
const RE_BLOCK_GRADIENT = /bg-gradient-to-/;
const RE_BLOCK_FROM = new RegExp(`from-(?:${BLOCK_COLORS})-`);
// Neutral "cards": white/gray rounded boxes with a border or shadow. Many
// sections (e.g. Key Findings) render each item as a neutral card with only a
// small colored severity badge — so a colored-only match misses them.
const RE_NEUTRAL_BG = /(?:^|\s)bg-white(?:\s|$)|bg-(?:gray|slate|zinc|neutral)-(?:50|100)(?:\s|$)/;
const EMPTY_REMOVED: Set<string> = new Set();

function isColoredBlock(el: Element): boolean {
  const c = typeof el.className === 'string' ? el.className : '';
  if (RE_BLOCK_BG.test(c)) return true;
  if (RE_BLOCK_BAR.test(c) && RE_BLOCK_BORDER.test(c)) return true;
  if (RE_BLOCK_GRADIENT.test(c) && RE_BLOCK_FROM.test(c)) return true;
  return false;
}

function isNeutralCard(el: Element): boolean {
  const c = typeof el.className === 'string' ? el.className : '';
  return RE_NEUTRAL_BG.test(c) && /\brounded/.test(c) && (/\bborder\b/.test(c) || /shadow/.test(c));
}

function isBlockCandidate(el: Element): boolean {
  return isColoredBlock(el) || isNeutralCard(el);
}

function blockSlug(t: string): string {
  return t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'block';
}

function blockLabel(el: Element): string {
  const heading = el.querySelector('h2, h3, h4, h5');
  const fromHeading = heading?.textContent?.trim();
  if (fromHeading) return fromHeading;
  const text = (el.textContent || '').trim().replace(/\s+/g, ' ');
  return text ? text.split(' ').slice(0, 6).join(' ') : 'Block';
}

/**
 * Wraps a section's content; discovers + (un)hides removable colored sub-blocks.
 * Renders the `.section-content` div the rest of the renderer/pagination expects.
 */
export const RemovableBlocksLayer: React.FC<{
  sectionId: string;
  removedBlocks?: Set<string>;
  onDiscoverBlocks?: (sectionId: string, blocks: DiscoveredBlock[]) => void;
  children: React.ReactNode;
}> = ({ sectionId, removedBlocks, onDiscoverBlocks, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  // Runs after every commit so re-created nodes get re-tagged + re-hidden.
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const removed = removedBlocks || EMPTY_REMOVED;
    const claimed: HTMLElement[] = [];
    const used = new Set<string>();
    const found: DiscoveredBlock[] = [];

    const candidates = Array.from(root.querySelectorAll<HTMLElement>('div')).filter(isBlockCandidate);
    for (const el of candidates) {
      // Outermost-first: skip a block nested inside one we already took
      // (so a colored block's inner white stat cards aren't separately removable).
      if (claimed.some((c) => c.contains(el))) continue;
      // A NEUTRAL card that itself wraps multiple card candidates is a container
      // (e.g. a grid of cards) — skip it so its child cards are individually
      // removable instead. Colored blocks are always claimed (they legitimately
      // wrap inner white stat cards).
      if (!isColoredBlock(el)) {
        let nested = 0;
        for (const other of candidates) {
          if (other !== el && el.contains(other)) { nested++; if (nested >= 2) break; }
        }
        if (nested >= 2) continue;
      }
      if (!(el.textContent || '').trim()) continue;
      claimed.push(el);
      const label = blockLabel(el);
      let id = `${sectionId}::${blockSlug(label)}`;
      let n = 2;
      while (used.has(id)) id = `${sectionId}::${blockSlug(label)}-${n++}`;
      used.add(id);
      el.dataset.blockId = id;
      found.push({ id, title: label });
    }

    for (const el of claimed) {
      el.style.display = removed.has(el.dataset.blockId!) ? 'none' : '';
    }

    onDiscoverBlocks?.(sectionId, found);
  });

  return <div ref={ref} className="section-content">{children}</div>;
};

/**
 * Wrapper that auto-shrinks its child to fit the available width.
 *
 * If the child's natural width is <= maxWidth, renders as-is. If wider, applies
 * a CSS transform: scale(maxWidth/naturalWidth) — the visual width becomes
 * maxWidth while the layout reserves the proportionally-reduced height. This
 * lets very wide matrices (10+ columns or long cell values) fit landscape and
 * portrait pages without scrollbars, while keeping every column visible.
 *
 * The two-phase render's measureRef reads getBoundingClientRect() which
 * respects transforms, so pagination uses the post-scale height directly.
 */
export const FitToWidth: React.FC<{
  maxWidth: number;
  children: React.ReactNode;
  /** Don't shrink below this scale; longer rows just wrap at this minimum. */
  minScale?: number;
}> = ({ maxWidth, children, minScale = 0.6 }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!innerRef.current) return;
    const naturalWidth = innerRef.current.scrollWidth;
    const naturalHeight = innerRef.current.scrollHeight;
    if (naturalWidth <= maxWidth || maxWidth <= 0) {
      setScale(1);
      setInnerHeight(null);
    } else {
      const s = Math.max(minScale, maxWidth / naturalWidth);
      setScale(s);
      setInnerHeight(Math.ceil(naturalHeight * s));
    }
  });

  return (
    <div
      ref={wrapperRef}
      style={{
        width: maxWidth,
        height: innerHeight ?? undefined,
        overflow: 'hidden',
      }}
    >
      <div
        ref={innerRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: scale < 1 ? maxWidth / scale : '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
};

interface SectionRendererProps {
  section: SectionData;
  data: any; // UniversalJsonResponse
  contentWidth: number;
  isEditMode?: boolean;
  editedContent?: Map<string, string>;
  onContentChange?: (contentPath: string, value: string) => void;
  removedBlocks?: Set<string>;
  onDiscoverBlocks?: (sectionId: string, blocks: DiscoveredBlock[]) => void;
}

// Editable text component
export interface EditableTextProps {
  contentPath: string;
  originalValue: string;
  editedContent?: Map<string, string>;
  onContentChange?: (contentPath: string, value: string) => void;
  isEditMode?: boolean;
  className?: string;
  as?: 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h4';
}

export const EditableText: React.FC<EditableTextProps> = ({
  contentPath,
  originalValue,
  editedContent,
  onContentChange,
  isEditMode,
  className = '',
  as: Component = 'span'
}) => {
  const ref = useRef<HTMLElement>(null);
  const currentValue = editedContent?.get(contentPath) ?? originalValue;

  const handleBlur = useCallback(() => {
    if (ref.current && onContentChange) {
      const newValue = ref.current.innerText;
      if (newValue !== originalValue) {
        onContentChange(contentPath, newValue);
      }
    }
  }, [contentPath, originalValue, onContentChange]);

  if (isEditMode) {
    return (
      <Component
        ref={ref as any}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        className={`${className} outline-none focus:bg-amber-50 focus:ring-2 focus:ring-amber-300 rounded px-0.5 -mx-0.5`}
        style={{ minHeight: '1em' }}
      >
        {currentValue}
      </Component>
    );
  }

  return <Component className={className}>{currentValue}</Component>;
};

// Helper to extract text from CitationText objects
const extractText = (content: any): string => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (content.value) return content.value; // Matrix cells use .value
  if (content.text) return content.text;   // Other content uses .text
  if (Array.isArray(content)) return content.map(extractText).join(' ');
  return '';
};

// Helper to render citation text with proper formatting
const renderCitationText = (content: any): React.ReactNode => {
  const text = extractText(content);
  // Split into paragraphs and render
  return text.split('\n').map((para, i) => (
    para.trim() ? <p key={i} className="mb-2">{para}</p> : null
  ));
};

// Shared edit props interface for section components
interface SectionEditProps {
  isEditMode?: boolean;
  editedContent?: Map<string, string>;
  onContentChange?: (contentPath: string, value: string) => void;
}

// Executive Summary Section
const ExecutiveSummarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const summary = data?.executiveSummary;
  if (!summary) return null;

  // Check if this is loss_run_analyzer format (has portfolioSnapshot/keyFindings/lossDrivers)
  const isLossRunAnalyzer = summary.portfolioSnapshot || summary.keyFindings || summary.lossDrivers;

  if (isLossRunAnalyzer) {
    return (
      <div className="executive-summary-content space-y-6">
        {/* Portfolio Snapshot */}
        {summary.portfolioSnapshot && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Portfolio Snapshot</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {summary.portfolioSnapshot.totalClaims !== undefined && (
                <div><span className="text-gray-600">Total Claims:</span> <strong>{summary.portfolioSnapshot.totalClaims}</strong></div>
              )}
              {summary.portfolioSnapshot.totalIncurred && (
                <div><span className="text-gray-600">Total Incurred:</span> <strong>{summary.portfolioSnapshot.totalIncurred}</strong></div>
              )}
              {summary.portfolioSnapshot.openClaimsCount !== undefined && (
                <div><span className="text-gray-600">Open Claims:</span> <strong>{summary.portfolioSnapshot.openClaimsCount}</strong></div>
              )}
              {summary.portfolioSnapshot.closedClaimsCount !== undefined && (
                <div><span className="text-gray-600">Closed Claims:</span> <strong>{summary.portfolioSnapshot.closedClaimsCount}</strong></div>
              )}
              {summary.portfolioSnapshot.dateRange && (
                <div><span className="text-gray-600">Date Range:</span> <strong>{summary.portfolioSnapshot.dateRange}</strong></div>
              )}
            </div>
          </div>
        )}

        {/* Trend Direction */}
        {summary.trendDirection && (
          <div className="text-lg font-medium">
            Trend: <span className={
              summary.trendDirection.toLowerCase() === 'improving' ? 'text-green-600' :
              summary.trendDirection.toLowerCase() === 'deteriorating' ? 'text-red-600' :
              'text-yellow-600'
            }>{summary.trendDirection}</span>
          </div>
        )}

        {/* Key Findings */}
        {summary.keyFindings && summary.keyFindings.length > 0 && (
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h3 className="font-semibold text-amber-800 mb-3">Key Findings</h3>
            <div className="space-y-3">
              {summary.keyFindings.map((item: any, idx: number) => {
                // Use extractText helper for consistent text extraction
                // Schema structure: { finding: { text, citations }, impact }
                let findingText = '';

                if (typeof item === 'string') {
                  findingText = item;
                } else if (item.finding) {
                  // finding is { text, citations } structure
                  findingText = extractText(item.finding);
                } else if (item.text) {
                  findingText = item.text;
                } else {
                  // Last resort: stringify the object without impact
                  const { impact, ...rest } = item;
                  findingText = extractText(rest) || JSON.stringify(rest);
                }

                const impact = item.impact || 'Low';

                return (
                  <div key={idx} className="p-3 bg-white rounded border border-amber-100 mb-2" style={{ pageBreakInside: 'avoid' }}>
                    <div className="mb-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        impact.toLowerCase() === 'high' ? 'bg-red-100 text-red-800' :
                        impact.toLowerCase() === 'medium' || impact.toLowerCase() === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>{impact} Impact</span>
                    </div>
                    <div
                      className="text-gray-700 text-sm"
                      style={{
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%'
                      }}
                    >
                      {findingText || <span className="italic text-gray-400">No finding text available</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Loss Drivers */}
        {summary.lossDrivers && summary.lossDrivers.length > 0 && (
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-3">Loss Drivers</h3>
            <div className="flex flex-wrap gap-2">
              {summary.lossDrivers.map((driver: string, idx: number) => (
                <span key={idx} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">{driver}</span>
              ))}
            </div>
          </div>
        )}

        {/* Analyst Notes */}
        {summary.analystNotes && (
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2">Analyst Notes</h3>
            <p className="text-gray-700">
              {typeof summary.analystNotes === 'object' && summary.analystNotes?.text
                ? summary.analystNotes.text
                : summary.analystNotes}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Check if this is YOY Renewal format (has renewalOverview/recommendedPathway/topAdvantages)
  const isYoyRenewal = summary.renewalOverview || summary.recommendedPathway ||
    (summary.recommendation && summary.topAdvantages);

  if (isYoyRenewal) {
    return (
      <div className="executive-summary-content space-y-6">
        {/* Analysis Track */}
        {summary.analysisTrack && (
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-2">Analysis Track</h3>
            <p className="text-gray-700">
              {extractText(summary.analysisTrack)}
            </p>
          </div>
        )}

        {/* Renewal Overview */}
        {summary.renewalOverview && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Renewal Overview</h3>
            <p className="text-gray-700">
              {extractText(summary.renewalOverview)}
            </p>
          </div>
        )}

        {/* Financial Summary */}
        {summary.financialSummary && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">Financial Summary</h3>
            <p className="text-gray-700">
              {extractText(summary.financialSummary)}
            </p>
          </div>
        )}

        {/* Top Advantages */}
        {summary.topAdvantages && summary.topAdvantages.length > 0 && (
          <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
            <h3 className="font-semibold text-emerald-800 mb-3">Top Advantages</h3>
            <ul className="space-y-2">
              {summary.topAdvantages.map((advantage: any, idx: number) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span className="text-gray-700">{extractText(advantage)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Top Concerns */}
        {summary.topConcerns && summary.topConcerns.length > 0 && (
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h3 className="font-semibold text-amber-800 mb-3">Top Concerns</h3>
            <ul className="space-y-2">
              {summary.topConcerns.map((concern: any, idx: number) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚠</span>
                  <span className="text-gray-700">{extractText(concern)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Strategic Trade-Off */}
        {summary.strategicTradeOff && (
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2">Strategic Trade-Off</h3>
            <p className="text-gray-700">
              {extractText(summary.strategicTradeOff)}
            </p>
          </div>
        )}

        {/* Recommendation */}
        {summary.recommendation && (
          <div className="bg-blue-100 p-4 rounded-lg border border-blue-300">
            <h3 className="font-semibold text-blue-900 mb-2">Recommendation</h3>
            <p className="text-gray-700">
              {extractText(summary.recommendation)}
            </p>
          </div>
        )}

        {/* Recommended Pathway */}
        {summary.recommendedPathway && (
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-lg text-white">
            <h3 className="font-semibold mb-1">Recommended Pathway</h3>
            <p className="text-xl font-bold">{summary.recommendedPathway}</p>
          </div>
        )}
      </div>
    );
  }

  // Check if this is Financial Analysis format (has text, financialCondition, trajectory)
  const isFinancialAnalysis = summary.text && (summary.financialCondition || summary.trajectory);

  if (isFinancialAnalysis) {
    return (
      <div className="executive-summary-content space-y-6">
        {/* Main Summary Text */}
        <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-500">
          <p className="text-gray-700 whitespace-pre-wrap">{summary.text}</p>
        </div>

        {/* Financial Status Cards */}
        <div className="grid grid-cols-3 gap-4">
          {summary.financialCondition && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Financial Condition</div>
              <div className={`text-lg font-semibold ${
                summary.financialCondition.toLowerCase().includes('strong') || summary.financialCondition.toLowerCase().includes('healthy') ? 'text-green-600' :
                summary.financialCondition.toLowerCase().includes('weak') || summary.financialCondition.toLowerCase().includes('poor') ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                {summary.financialCondition}
              </div>
            </div>
          )}
          {summary.trajectory && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Trajectory</div>
              <div className={`text-lg font-semibold ${
                summary.trajectory.toLowerCase().includes('improv') || summary.trajectory.toLowerCase().includes('upward') ? 'text-green-600' :
                summary.trajectory.toLowerCase().includes('declin') || summary.trajectory.toLowerCase().includes('downward') ? 'text-red-600' :
                'text-gray-700'
              }`}>
                {summary.trajectory}
              </div>
            </div>
          )}
          {summary.recommendation && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Recommendation</div>
              <div className={`text-lg font-semibold ${
                summary.recommendation.toLowerCase().includes('accept') || summary.recommendation.toLowerCase().includes('approve') ? 'text-green-600' :
                summary.recommendation.toLowerCase().includes('decline') || summary.recommendation.toLowerCase().includes('reject') ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                {summary.recommendation}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Standard format with paragraphs
  if (!summary.paragraphs) return null;

  return (
    <div className="executive-summary-content">
      {summary.paragraphs.map((para: any, index: number) => (
        <div key={index} className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r">
          <EditableText
            contentPath={`executiveSummary.paragraphs.${index}.content`}
            originalValue={extractText(para.content)}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
            as="div"
            className="text-gray-700"
          />
        </div>
      ))}
    </div>
  );
};

// Analysis Matrix Section
const AnalysisMatrixSection: React.FC<{ matrix: any; title: string; matrixIndex: number; contentWidth?: number } & SectionEditProps> = ({
  matrix,
  title,
  matrixIndex,
  contentWidth,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  if (!matrix) {
    return (
      <div className="text-gray-500 italic p-4 bg-gray-50 rounded">
        Matrix data not found
      </div>
    );
  }

  // Check if we have headers and rows
  const headers = matrix.headers || matrix.columns || matrix.columnHeaders;
  const rows = matrix.rows || matrix.data || matrix.items;

  const hasHeaders = headers && Array.isArray(headers) && headers.length > 0;
  const hasRows = rows && Array.isArray(rows) && rows.length > 0;

  if (!hasHeaders || !hasRows) {
    return (
      <div className="text-gray-500 italic p-4 bg-gray-50 rounded">
        Matrix is empty (no headers or rows). Keys found: {Object.keys(matrix).join(', ')}
      </div>
    );
  }

  return (
    <div className="analysis-matrix-content">
      {matrix.description && (
        <EditableText
          contentPath={`analysisMatrices.${matrixIndex}.description`}
          originalValue={extractText(matrix.description)}
          editedContent={editedContent}
          onContentChange={onContentChange}
          isEditMode={isEditMode}
          as="p"
          className="text-gray-600 mb-4"
        />
      )}

      <FitToWidth maxWidth={contentWidth ?? 1000} minScale={0.45}>
        <table
          className="border-collapse border border-gray-300 bg-white shadow-sm pdf-matrix-table"
          style={{
            // Natural table layout + FitToWidth scaling:
            // - tableLayout: 'auto' lets columns size to content (no awkward word-wrap mid-word)
            // - whiteSpace: 'normal' lets natural spaces wrap at word boundaries
            // - FitToWidth scales the whole table down if total natural width > page
            // This avoids "SILV / ERA / DO" word-shredding while keeping the table on one page.
            tableLayout: 'auto',
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr className="bg-gray-100">
              {headers.map((header: string, i: number) => (
                <th
                  key={i}
                  className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-800 align-top"
                >
                  <EditableText
                    contentPath={`analysisMatrices.${matrixIndex}.headers.${i}`}
                    originalValue={header}
                    editedContent={editedContent}
                    onContentChange={onContentChange}
                    isEditMode={isEditMode}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any, rowIndex: number) => {
              // Normalize cells-array shape (yoy_renewal add-ons + policy_comparison)
              // into a {column: value} dict so the existing object-row path handles it.
              let normalizedRow = row;
              if (row && typeof row === 'object' && !Array.isArray(row) && Array.isArray(row.cells)) {
                const dict: Record<string, any> = {};
                for (const c of row.cells) {
                  if (c && typeof c === 'object' && typeof c.column === 'string') {
                    dict[c.column] = c.value ?? '';
                  }
                }
                normalizedRow = dict;
              }
              return (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {Array.isArray(normalizedRow) ? (
                  normalizedRow.map((cell: any, cellIndex: number) => (
                    <td
                      key={cellIndex}
                      className="border border-gray-300 px-3 py-2 text-sm text-gray-700 align-top"
                    >
                      <EditableText
                        contentPath={`analysisMatrices.${matrixIndex}.rows.${rowIndex}.${cellIndex}`}
                        originalValue={extractText(cell)}
                        editedContent={editedContent}
                        onContentChange={onContentChange}
                        isEditMode={isEditMode}
                      />
                    </td>
                  ))
                ) : typeof normalizedRow === 'object' ? (
                  headers.map((header: string, cellIndex: number) => {
                    const cellValue = normalizedRow[header] ??
                      normalizedRow[header.toLowerCase()] ??
                      Object.values(normalizedRow)[cellIndex] ?? '';
                    return (
                      <td
                        key={cellIndex}
                        className="border border-gray-300 px-3 py-2 text-sm text-gray-700 align-top"
                        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        <EditableText
                          contentPath={`analysisMatrices.${matrixIndex}.rows.${rowIndex}.${header}`}
                          originalValue={extractText(cellValue)}
                          editedContent={editedContent}
                          onContentChange={onContentChange}
                          isEditMode={isEditMode}
                        />
                      </td>
                    );
                  })
                ) : (
                  // Single value row (shouldn't happen but handle gracefully)
                  <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700" colSpan={headers.length}>
                    {extractText(normalizedRow)}
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </FitToWidth>
    </div>
  );
};

// Key Findings Section
const KeyFindingsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  if (!data?.keyFindings) return null;

  // Check if keyFindings is Financial Analysis format (array with {finding, metric, severity})
  const isFinancialAnalysisFormat = Array.isArray(data.keyFindings) &&
    data.keyFindings.length > 0 &&
    data.keyFindings[0]?.finding !== undefined;

  if (isFinancialAnalysisFormat) {
    return (
      <div className="key-findings-content space-y-3">
        {(data.keyFindings as any[]).map((item: any, idx: number) => (
          <div key={idx} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <span className="font-medium text-gray-800 flex-1">{item.finding}</span>
              {item.severity && (
                <span className={`ml-3 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                  item.severity.toLowerCase().includes('high') || item.severity.toLowerCase().includes('critical') ? 'bg-red-100 text-red-800' :
                  item.severity.toLowerCase().includes('low') ? 'bg-green-100 text-green-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {item.severity}
                </span>
              )}
            </div>
            {item.metric && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Metric:</span> {item.metric}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  const { summary, findings } = data.keyFindings;

  return (
    <div className="key-findings-content">
      {summary && (
        <div className="mb-6 p-4 bg-gray-50 rounded">
          <EditableText
            contentPath="keyFindings.summary"
            originalValue={extractText(summary)}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
            as="div"
          />
        </div>
      )}

      {findings?.map((finding: any, index: number) => (
        <div key={index} className="mb-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-start gap-3">
            <span className={`px-2 py-1 text-xs font-bold rounded ${
              finding.impact === 'Critical' || finding.impact === 'Negative'
                ? 'bg-red-100 text-red-800'
                : finding.impact === 'Positive'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {finding.category || 'Finding'}
            </span>
            {finding.priority && (
              <span className={`px-2 py-1 text-xs font-bold rounded ${
                finding.priority === 'High' || finding.priority === 'Immediate'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {finding.priority}
              </span>
            )}
          </div>

          {finding.title && (
            <EditableText
              contentPath={`keyFindings.findings.${index}.title`}
              originalValue={finding.title}
              editedContent={editedContent}
              onContentChange={onContentChange}
              isEditMode={isEditMode}
              as="h4"
              className="font-semibold text-gray-800 mt-2"
            />
          )}

          <div className="mt-2 text-gray-700">
            {finding.finding?.observation && (
              <div className="mb-2">
                <strong className="text-sm text-gray-500">Observation:</strong>
                <EditableText
                  contentPath={`keyFindings.findings.${index}.finding.observation`}
                  originalValue={extractText(finding.finding.observation)}
                  editedContent={editedContent}
                  onContentChange={onContentChange}
                  isEditMode={isEditMode}
                  as="div"
                />
              </div>
            )}
            {finding.finding?.implication && (
              <div className="mb-2">
                <strong className="text-sm text-gray-500">Implication:</strong>
                <EditableText
                  contentPath={`keyFindings.findings.${index}.finding.implication`}
                  originalValue={extractText(finding.finding.implication)}
                  editedContent={editedContent}
                  onContentChange={onContentChange}
                  isEditMode={isEditMode}
                  as="div"
                />
              </div>
            )}
            {finding.finding?.recommendation && (
              <div className="mb-2">
                <strong className="text-sm text-gray-500">Recommendation:</strong>
                <EditableText
                  contentPath={`keyFindings.findings.${index}.finding.recommendation`}
                  originalValue={extractText(finding.finding.recommendation)}
                  editedContent={editedContent}
                  onContentChange={onContentChange}
                  isEditMode={isEditMode}
                  as="div"
                />
              </div>
            )}
            {finding.description && !finding.finding && (
              <EditableText
                contentPath={`keyFindings.findings.${index}.description`}
                originalValue={extractText(finding.description)}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
                as="div"
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Recommendations Section
const RecommendationsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const recommendations = data?.recommendations;
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div className="recommendations-content">
      {recommendations.map((rec: any, index: number) => (
        <div key={index} className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-r">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-1 text-xs font-bold rounded ${
              rec.priority === 'Critical' ? 'bg-red-200 text-red-900' :
              rec.priority === 'High' ? 'bg-orange-200 text-orange-900' :
              rec.priority === 'Medium' ? 'bg-yellow-200 text-yellow-900' :
              'bg-green-200 text-green-900'
            }`}>
              {rec.priority}
            </span>
            <span className="text-sm text-gray-600">{rec.category}</span>
          </div>

          <div className="font-medium text-gray-800 mb-2">
            <EditableText
              contentPath={`recommendations.${index}.action`}
              originalValue={extractText(rec.action)}
              editedContent={editedContent}
              onContentChange={onContentChange}
              isEditMode={isEditMode}
              as="div"
            />
          </div>

          <div className="text-sm text-gray-600">
            <strong>Rationale:</strong>{' '}
            <EditableText
              contentPath={`recommendations.${index}.rationale`}
              originalValue={extractText(rec.rationale)}
              editedContent={editedContent}
              onContentChange={onContentChange}
              isEditMode={isEditMode}
            />
          </div>

          {rec.timeline && (
            <div className="text-sm text-gray-500 mt-2">
              <strong>Timeline:</strong>{' '}
              <EditableText
                contentPath={`recommendations.${index}.timeline`}
                originalValue={rec.timeline}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Per-item recommendation — renders a single recommendation card so the V2
// paginator can place page breaks between items rather than mid-card.
const SingleRecommendationSection: React.FC<{ data: any; index: number } & SectionEditProps> = ({
  data,
  index,
  isEditMode,
  editedContent,
  onContentChange,
}) => {
  const rec = data?.recommendations?.[index];
  if (!rec) return null;
  return (
    <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-r">
      <div className="flex items-center gap-2 mb-2">
        <span className={`px-2 py-1 text-xs font-bold rounded ${
          rec.priority === 'Critical' ? 'bg-red-200 text-red-900' :
          rec.priority === 'High' ? 'bg-orange-200 text-orange-900' :
          rec.priority === 'Medium' ? 'bg-yellow-200 text-yellow-900' :
          'bg-green-200 text-green-900'
        }`}>
          {rec.priority}
        </span>
        {rec.category && <span className="text-sm text-gray-600">{rec.category}</span>}
      </div>

      {rec.action && (
        <div className="font-medium text-gray-800 mb-2">
          <EditableText
            contentPath={`recommendations.${index}.action`}
            originalValue={extractText(rec.action)}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
            as="div"
          />
        </div>
      )}

      {rec.rationale && (
        <div className="text-sm text-gray-600">
          <strong>Rationale:</strong>{' '}
          <EditableText
            contentPath={`recommendations.${index}.rationale`}
            originalValue={extractText(rec.rationale)}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
          />
        </div>
      )}

      {rec.expectedOutcome && (
        <div className="text-sm text-gray-600 mt-2">
          <strong>Expected Outcome:</strong>{' '}
          <EditableText
            contentPath={`recommendations.${index}.expectedOutcome`}
            originalValue={extractText(rec.expectedOutcome)}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
          />
        </div>
      )}

      {rec.timeline && (
        <div className="text-sm text-gray-500 mt-2">
          <strong>Timeline:</strong>{' '}
          <EditableText
            contentPath={`recommendations.${index}.timeline`}
            originalValue={rec.timeline}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
          />
        </div>
      )}
    </div>
  );
};

// Strategic Considerations Section
const StrategicConsiderationsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const considerations = data?.strategicConsiderations;
  if (!considerations || considerations.length === 0) return null;

  return (
    <div className="strategic-considerations-content">
      {considerations.map((item: any, index: number) => (
        <div key={index} className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <EditableText
            contentPath={`strategicConsiderations.${index}.consideration`}
            originalValue={item.consideration}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
            as="h4"
            className="font-bold text-gray-800 mb-2"
          />
          <EditableText
            contentPath={`strategicConsiderations.${index}.analysis`}
            originalValue={extractText(item.analysis)}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
            as="div"
            className="text-gray-700 mb-2"
          />
          {item.recommendation && (
            <div className="mt-2 p-2 bg-purple-100 rounded text-sm">
              <strong>Recommendation:</strong>{' '}
              <EditableText
                contentPath={`strategicConsiderations.${index}.recommendation`}
                originalValue={extractText(item.recommendation)}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Risk Mitigation Section
const RiskMitigationSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const strategies = data?.riskMitigationStrategies;
  if (!strategies || strategies.length === 0) return null;

  return (
    <div className="risk-mitigation-content">
      {strategies.map((strategy: any, index: number) => (
        <div key={index} className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <EditableText
            contentPath={`riskMitigationStrategies.${index}.risk`}
            originalValue={strategy.risk}
            editedContent={editedContent}
            onContentChange={onContentChange}
            isEditMode={isEditMode}
            as="h4"
            className="font-bold text-gray-800 mb-2"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <strong className="text-sm text-gray-500">Strategy:</strong>
              <EditableText
                contentPath={`riskMitigationStrategies.${index}.strategy`}
                originalValue={extractText(strategy.strategy)}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
                as="div"
                className="text-gray-700"
              />
            </div>
            <div>
              <strong className="text-sm text-gray-500">Implementation:</strong>
              <EditableText
                contentPath={`riskMitigationStrategies.${index}.implementation`}
                originalValue={extractText(strategy.implementation)}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
                as="div"
                className="text-gray-700"
              />
            </div>
          </div>
          {strategy.expectedReduction && (
            <div className="mt-2 pt-2 border-t border-red-200">
              <strong className="text-sm text-gray-500">Expected Reduction:</strong>
              <EditableText
                contentPath={`riskMitigationStrategies.${index}.expectedReduction`}
                originalValue={strategy.expectedReduction}
                editedContent={editedContent}
                onContentChange={onContentChange}
                isEditMode={isEditMode}
                className="ml-2 text-gray-700"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// === Loss Run Analyzer Sections ===

// Document Manifest Section
const DocumentManifestSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const manifest = data?.documentManifest;
  if (!manifest || !Array.isArray(manifest)) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carrier</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy #</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line of Business</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claims</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {manifest.map((doc: any, index: number) => (
            <tr key={index}>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`documentManifest.${index}.documentId`} originalValue={doc.documentId || `Document ${index + 1}`} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`documentManifest.${index}.carrier`} originalValue={doc.carrier || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`documentManifest.${index}.policyNumber`} originalValue={doc.policyNumber || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`documentManifest.${index}.lineOfBusiness`} originalValue={doc.lineOfBusiness || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`documentManifest.${index}.claimCount`} originalValue={String(doc.claimCount ?? '-')} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Claims Table Section
const ClaimsTableSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const claims = data?.claims;
  if (!claims || !Array.isArray(claims)) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Claim #</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date of Loss</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Incurred</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {claims.map((claim: any, index: number) => (
            <tr key={index}>
              <td className="px-3 py-2 font-medium"><EditableText contentPath={`claims.${index}.claimNumber`} originalValue={claim.claimNumber || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
              <td className="px-3 py-2"><EditableText contentPath={`claims.${index}.claimStatus`} originalValue={claim.claimStatus || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-3 py-2"><EditableText contentPath={`claims.${index}.dateOfLoss`} originalValue={claim.dateOfLoss || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-3 py-2 max-w-xs"><EditableText contentPath={`claims.${index}.description`} originalValue={claim.description || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-3 py-2 text-right font-medium"><EditableText contentPath={`claims.${index}.totalIncurred`} originalValue={claim.totalIncurred || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Validation Results Section
const ValidationResultsSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const results = data?.validationResults;
  if (!results || !Array.isArray(results)) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source Total</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Extracted Sum</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {results.map((result: any, index: number) => (
            <tr key={index}>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`validationResults.${index}.documentId`} originalValue={result.documentId || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`validationResults.${index}.extractedRecordCount`} originalValue={String(result.extractedRecordCount ?? '-')} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`validationResults.${index}.sourceTotalIncurred`} originalValue={result.sourceTotalIncurred || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`validationResults.${index}.extractedSum`} originalValue={result.extractedSum || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  result.status === 'VALIDATED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  <EditableText contentPath={`validationResults.${index}.status`} originalValue={result.status || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Claim Development Section
const ClaimDevelopmentSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const development = data?.claimDevelopment;
  if (!development || !Array.isArray(development)) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claim #</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date of Loss</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Development</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% Change</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {development.map((item: any, index: number) => (
            <tr key={index}>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`claimDevelopment.${index}.claimNumber`} originalValue={item.claimNumber || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`claimDevelopment.${index}.dateOfLoss`} originalValue={item.dateOfLoss || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm text-right"><EditableText contentPath={`claimDevelopment.${index}.totalDevelopment`} originalValue={item.totalDevelopment || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
              <td className="px-4 py-3 text-sm text-right"><EditableText contentPath={`claimDevelopment.${index}.percentageChange`} originalValue={item.percentageChange || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Frequency Analysis Section
const FrequencyAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const freq = data?.frequencyAnalysis;
  if (!freq) return null;

  return (
    <div className="space-y-6">
      {freq.claimsByLOB && Array.isArray(freq.claimsByLOB) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Claims by Line of Business</h4>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">LOB</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {freq.claimsByLOB.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="px-4 py-2 text-sm"><EditableText contentPath={`frequencyAnalysis.claimsByLOB.${index}.lob`} originalValue={item.lob || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`frequencyAnalysis.claimsByLOB.${index}.count`} originalValue={String(item.count ?? '-')} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`frequencyAnalysis.claimsByLOB.${index}.percentage`} originalValue={item.percentage || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {freq.claimsByState && Array.isArray(freq.claimsByState) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Claims by State</h4>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {freq.claimsByState.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="px-4 py-2 text-sm"><EditableText contentPath={`frequencyAnalysis.claimsByState.${index}.state`} originalValue={item.state || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`frequencyAnalysis.claimsByState.${index}.count`} originalValue={String(item.count ?? '-')} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`frequencyAnalysis.claimsByState.${index}.percentage`} originalValue={item.percentage || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Severity Analysis Section
const SeverityAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const severity = data?.severityAnalysis;
  if (!severity) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-600 font-medium">Average Claim Size</div>
          <div className="text-xl font-bold"><EditableText contentPath="severityAnalysis.averageClaimSize" originalValue={severity.averageClaimSize || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-blue-800" /></div>
        </div>
        {severity.medianClaimSize && (
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 font-medium">Median Claim Size</div>
            <div className="text-xl font-bold"><EditableText contentPath="severityAnalysis.medianClaimSize" originalValue={severity.medianClaimSize} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-green-800" /></div>
          </div>
        )}
      </div>

      {severity.distribution && Array.isArray(severity.distribution) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Severity Distribution</h4>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Incurred</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {severity.distribution.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="px-4 py-2 text-sm"><EditableText contentPath={`severityAnalysis.distribution.${index}.category`} originalValue={item.category || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`severityAnalysis.distribution.${index}.count`} originalValue={String(item.count ?? '-')} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`severityAnalysis.distribution.${index}.totalIncurred`} originalValue={item.totalIncurred || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                  <td className="px-4 py-2 text-sm text-right"><EditableText contentPath={`severityAnalysis.distribution.${index}.percentage`} originalValue={item.percentage || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Top Claims Section
const TopClaimsSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const topClaims = data?.topClaims;
  if (!topClaims || !Array.isArray(topClaims)) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claim #</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date of Loss</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Incurred</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {topClaims.map((claim: any, index: number) => (
            <tr key={index}>
              <td className="px-4 py-3 text-sm font-bold text-gray-900">#{claim.rank || index + 1}</td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`topClaims.${index}.claimNumber`} originalValue={claim.claimNumber || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
              <td className="px-4 py-3 text-sm"><EditableText contentPath={`topClaims.${index}.dateOfLoss`} originalValue={claim.dateOfLoss || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm max-w-xs"><EditableText contentPath={`topClaims.${index}.description`} originalValue={claim.description || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" /></td>
              <td className="px-4 py-3 text-sm text-right font-bold"><EditableText contentPath={`topClaims.${index}.totalIncurred`} originalValue={claim.totalIncurred || '-'} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-900" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Data Quality Section
const DataQualitySection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const quality = data?.dataQuality;
  if (!quality) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className={`p-4 rounded-lg ${quality.allRecordsExtracted ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="text-sm font-medium">All Records Extracted</div>
          <div className={`text-lg font-bold ${quality.allRecordsExtracted ? 'text-green-800' : 'text-yellow-800'}`}>
            {quality.allRecordsExtracted ? '✓ Yes' : '⚠ No'}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${quality.financialCrossFootValid ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="text-sm font-medium">Financial Cross-Foot Valid</div>
          <div className={`text-lg font-bold ${quality.financialCrossFootValid ? 'text-green-800' : 'text-yellow-800'}`}>
            {quality.financialCrossFootValid ? '✓ Yes' : '⚠ No'}
          </div>
        </div>
      </div>

      {quality.issues && Array.isArray(quality.issues) && quality.issues.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Issues Found</h4>
          <ul className="list-disc pl-5 space-y-1">
            {quality.issues.map((issue: string, index: number) => (
              <li key={index} className="text-sm"><EditableText contentPath={`dataQuality.issues.${index}`} originalValue={issue} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-yellow-700" /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Strategic Action Plan Section (YOY Renewal)
const StrategicActionPlanSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const plan = data?.strategicActionPlan;
  if (!plan) return null;

  const renderActionList = (actions: any[], title: string, colorClass: string, listKey: string) => {
    if (!actions || actions.length === 0) return null;
    return (
      <div className={`bg-${colorClass}-50 p-4 rounded-lg border border-${colorClass}-200 mb-4`}>
        <h4 className={`font-semibold text-${colorClass}-800 mb-3`}>{title}</h4>
        <div className="space-y-2">
          {actions.map((action: any, idx: number) => (
            <div key={idx} className="bg-white p-3 rounded border border-gray-100">
              <EditableText contentPath={`strategicActionPlan.${listKey}.${idx}.action`} originalValue={action.action || extractText(action)} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="div" className="text-gray-700" />
              {(action.owner || action.deadline || action.priority) && (
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  {action.owner && <span className="text-gray-500">Owner: {action.owner}</span>}
                  {action.deadline && <span className="text-gray-500">Deadline: {action.deadline}</span>}
                  {action.priority && (
                    <span className={`px-2 py-0.5 rounded ${
                      action.priority.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' :
                      action.priority.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>{action.priority}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Selected Pathway */}
      {plan.selectedPathway && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 rounded-lg text-white mb-4">
          <h4 className="font-semibold mb-1">Selected Pathway</h4>
          <EditableText contentPath="strategicActionPlan.selectedPathway" originalValue={plan.selectedPathway} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="p" className="text-xl font-bold" />
        </div>
      )}

      {renderActionList(plan.immediateActions, 'Immediate Actions', 'red', 'immediateActions')}
      {renderActionList(plan.shortTermActions, 'Short-Term Actions', 'orange', 'shortTermActions')}
      {renderActionList(plan.mediumTermActions, 'Medium-Term Actions', 'yellow', 'mediumTermActions')}
      {renderActionList(plan.longTermInitiatives, 'Long-Term Initiatives', 'blue', 'longTermInitiatives')}

      {/* Success Metrics */}
      {plan.successMetrics && plan.successMetrics.length > 0 && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-3">Success Metrics</h4>
          <ul className="space-y-1">
            {plan.successMetrics.map((metric: string, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <EditableText contentPath={`strategicActionPlan.successMetrics.${idx}`} originalValue={metric} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next Review Date */}
      {plan.nextReviewDate && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">Next Review Date:</span> <EditableText contentPath="strategicActionPlan.nextReviewDate" originalValue={plan.nextReviewDate} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} />
        </div>
      )}
    </div>
  );
};

// Confidence Statement Section (YOY Renewal)
const ConfidenceStatementSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const statement = data?.confidenceStatement;
  if (!statement) return null;

  return (
    <div className="space-y-4">
      {/* Overall Confidence */}
      {statement.overallConfidence && (
        <div className={`p-4 rounded-lg border ${
          statement.overallConfidence.toLowerCase() === 'high' ? 'bg-green-50 border-green-200' :
          statement.overallConfidence.toLowerCase() === 'medium' ? 'bg-yellow-50 border-yellow-200' :
          'bg-red-50 border-red-200'
        }`}>
          <h4 className="font-semibold mb-2">Overall Confidence</h4>
          <p className={`text-xl font-bold ${
            statement.overallConfidence.toLowerCase() === 'high' ? 'text-green-700' :
            statement.overallConfidence.toLowerCase() === 'medium' ? 'text-yellow-700' :
            'text-red-700'
          }`}><EditableText contentPath="confidenceStatement.overallConfidence" originalValue={statement.overallConfidence} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} /></p>
        </div>
      )}

      {/* Basis for Confidence */}
      {statement.basisForConfidence && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-2">Basis for Confidence</h4>
          <EditableText contentPath="confidenceStatement.basisForConfidence" originalValue={extractText(statement.basisForConfidence)} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="p" className="text-gray-700" />
        </div>
      )}

      {/* Specific Limitations */}
      {statement.specificLimitations && statement.specificLimitations.length > 0 && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h4 className="font-semibold text-amber-800 mb-3">Specific Limitations</h4>
          <ul className="space-y-2">
            {statement.specificLimitations.map((limitation: any, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">⚠</span>
                <EditableText contentPath={`confidenceStatement.specificLimitations.${idx}`} originalValue={extractText(limitation)} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Verification Needed */}
      {statement.verificationNeeded && statement.verificationNeeded.length > 0 && (
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h4 className="font-semibold text-purple-800 mb-3">Verification Needed</h4>
          <ul className="space-y-1">
            {statement.verificationNeeded.map((item: string, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">•</span>
                <EditableText contentPath={`confidenceStatement.verificationNeeded.${idx}`} originalValue={item} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-700" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Research Overview Section
const ResearchOverviewSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const overview = data?.researchOverview;
  if (!overview) return null;

  return (
    <div className="space-y-4">
      {overview.researchScope && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Research Scope</h4>
          <EditableText contentPath="researchOverview.researchScope" originalValue={overview.researchScope} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="p" className="text-sm text-gray-600" />
        </div>
      )}

      {overview.keyObjectives && Array.isArray(overview.keyObjectives) && overview.keyObjectives.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Key Objectives</h4>
          <ul className="list-disc pl-5 space-y-1">
            {overview.keyObjectives.map((obj: string, idx: number) => (
              <li key={idx} className="text-sm"><EditableText contentPath={`researchOverview.keyObjectives.${idx}`} originalValue={obj} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-600" /></li>
            ))}
          </ul>
        </div>
      )}

      {overview.methodology && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Methodology</h4>
          <EditableText contentPath="researchOverview.methodology" originalValue={overview.methodology} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="p" className="text-sm text-gray-600" />
        </div>
      )}

      {overview.dataSourcesSummary && Array.isArray(overview.dataSourcesSummary) && overview.dataSourcesSummary.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Data Sources</h4>
          <ul className="list-disc pl-5 space-y-1">
            {overview.dataSourcesSummary.map((src: string, idx: number) => (
              <li key={idx} className="text-sm"><EditableText contentPath={`researchOverview.dataSourcesSummary.${idx}`} originalValue={src} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-600" /></li>
            ))}
          </ul>
        </div>
      )}

      {overview.limitations && Array.isArray(overview.limitations) && overview.limitations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Limitations</h4>
          <ul className="list-disc pl-5 space-y-1">
            {overview.limitations.map((lim: string, idx: number) => (
              <li key={idx} className="text-sm"><EditableText contentPath={`researchOverview.limitations.${idx}`} originalValue={lim} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-yellow-700" /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Data Quality Assessment Section (Policy/Document Comparison)
const DataQualityAssessmentSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const assessment = data?.dataQualityAssessment;
  if (!assessment) return null;

  return (
    <div className="space-y-4">
      {assessment.completenessScore && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-sm font-medium text-blue-700">Completeness Score</div>
          <div className="text-lg font-bold text-blue-800">{assessment.completenessScore}</div>
        </div>
      )}

      {assessment.missingElements && Array.isArray(assessment.missingElements) && assessment.missingElements.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Missing Elements</h4>
          <ul className="list-disc pl-5 space-y-1">
            {assessment.missingElements.map((el: string, idx: number) => (
              <li key={idx} className="text-sm"><EditableText contentPath={`dataQualityAssessment.missingElements.${idx}`} originalValue={el} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-yellow-700" /></li>
            ))}
          </ul>
        </div>
      )}

      {assessment.assumptionsMade && Array.isArray(assessment.assumptionsMade) && assessment.assumptionsMade.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Assumptions Made</h4>
          <ul className="list-disc pl-5 space-y-1">
            {assessment.assumptionsMade.map((a: string, idx: number) => (
              <li key={idx} className="text-sm"><EditableText contentPath={`dataQualityAssessment.assumptionsMade.${idx}`} originalValue={a} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-gray-600" /></li>
            ))}
          </ul>
        </div>
      )}

      {assessment.recommendedFollowUp && Array.isArray(assessment.recommendedFollowUp) && assessment.recommendedFollowUp.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Recommended Follow-Up</h4>
          <ul className="list-disc pl-5 space-y-1">
            {assessment.recommendedFollowUp.map((r: string, idx: number) => (
              <li key={idx} className="text-sm"><EditableText contentPath={`dataQualityAssessment.recommendedFollowUp.${idx}`} originalValue={r} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} className="text-blue-700" /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// YOY Renewal opt-in add-on sections (Report Options step in the modal)
// ============================================================================
// These sections live under reportAddOns.* — produced by a separate AI call
// because the base yoy_renewal schema is at Vertex's complexity ceiling.
//
// Citation shape is the same canonical { docId, docName, page, quote }
// pattern used everywhere else in the report (analysisMatrices etc.), so the
// PDF export inherits the citation-stripping behavior from extractText().

// Convert a cells-array row ({ cells: [{column, value, citations}] }) into a
// {column: value} dict so the existing table render can find values by header.
const cellsRowToDict = (row: any): Record<string, any> => {
  if (!row || typeof row !== 'object') return {};
  if (!Array.isArray(row.cells)) return row;
  const dict: Record<string, any> = {};
  for (const c of row.cells) {
    if (c && typeof c === 'object' && typeof c.column === 'string') {
      dict[c.column] = c.value ?? '';
    }
  }
  return dict;
};

const renderAddOnMatrix = (headers: string[], rows: any[], contentWidth?: number) => {
  if (!Array.isArray(headers) || !Array.isArray(rows) || rows.length === 0) return null;
  return (
    <FitToWidth maxWidth={contentWidth ?? 1000} minScale={0.45}>
      <table
        className="border-collapse border border-gray-300 bg-white shadow-sm mb-4 pdf-matrix-table"
        style={{ tableLayout: 'auto', borderSpacing: 0 }}
      >
        <thead>
          <tr className="bg-gray-100">
            {headers.map((h: string, i: number) => (
              <th
                key={i}
                className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-800 align-top"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, rowIdx: number) => {
            const dict = cellsRowToDict(row);
            return (
              <tr key={rowIdx} className="hover:bg-gray-50">
                {headers.map((h: string, cellIdx: number) => (
                  <td
                    key={cellIdx}
                    className="border border-gray-300 px-3 py-2 text-sm text-gray-700 align-top"
                  >
                    {extractText(dict[h] ?? '')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </FitToWidth>
  );
};

const SchedulesSection: React.FC<{ data: any; contentWidth?: number }> = ({ data, contentWidth }) => {
  // Tolerate every shape Gemini has produced for this field.
  const raw = data?.reportAddOns?.schedules;
  let scheduleList: any[] = [];
  if (Array.isArray(raw)) {
    scheduleList = raw.length > 0 && raw.every((s: any) => Array.isArray(s)) ? raw.flat() : raw;
  } else if (raw && Array.isArray(raw.schedules)) {
    scheduleList = raw.schedules;
  }
  if (scheduleList.length === 0) return null;
  return (
    <div className="space-y-6">
      {scheduleList.map((schedule: any, sIdx: number) => (
        <div key={sIdx} className="border border-gray-200 rounded">
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">{schedule.scheduleType}</h3>
            {schedule.policyDocName && (
              <div className="text-xs text-gray-500 mt-0.5">Source: {schedule.policyDocName}</div>
            )}
          </div>
          <div className="p-2">
            {renderAddOnMatrix(schedule.headers, schedule.rows, contentWidth)}
            {Array.isArray(schedule.additionsVsPriorPolicy) && schedule.additionsVsPriorPolicy.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-green-700 mb-1">Added vs prior policy</div>
                <ul className="list-disc list-inside text-sm text-gray-700">
                  {schedule.additionsVsPriorPolicy.map((a: string, i: number) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(schedule.removalsVsPriorPolicy) && schedule.removalsVsPriorPolicy.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-red-700 mb-1">Removed vs prior policy</div>
                <ul className="list-disc list-inside text-sm text-gray-700">
                  {schedule.removalsVsPriorPolicy.map((r: string, i: number) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const CoverageDetailsSection: React.FC<{ data: any; contentWidth?: number }> = ({ data, contentWidth }) => {
  const cds = data?.reportAddOns?.coverageDetails;
  if (!cds) return null;
  const matrixField = (label: string, m: any) => {
    if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
      return (
        <div className="mb-4" key={label}>
          <h3 className="text-base font-semibold mb-2 text-gray-900">{label}</h3>
          {renderAddOnMatrix(m.headers, m.rows, contentWidth)}
        </div>
      );
    }
    if (typeof m === 'string' && m.trim()) {
      return (
        <div className="mb-4" key={label}>
          <h3 className="text-base font-semibold mb-2 text-gray-900">{label}</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{m}</p>
        </div>
      );
    }
    return null;
  };
  return (
    <div className="space-y-2">
      {matrixField('Deductibles & Retentions', cds.deductiblesAndRetentions)}
      {matrixField('Sub-Limits', cds.subLimits)}
      {matrixField('Enhancement Endorsement Limits', cds.enhancementEndorsementLimits)}
      {Array.isArray(cds.exclusionsAdded) && cds.exclusionsAdded.length > 0 && (
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2 text-gray-900">Exclusions Added</h3>
          <ul className="list-disc list-inside text-sm text-gray-700">
            {cds.exclusionsAdded.map((e: any, i: number) => (
              <li key={i}>{typeof e === 'string' ? e : extractText(e)}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(cds.exclusionsRemoved) && cds.exclusionsRemoved.length > 0 && (
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2 text-gray-900">Exclusions Removed</h3>
          <ul className="list-disc list-inside text-sm text-gray-700">
            {cds.exclusionsRemoved.map((e: any, i: number) => (
              <li key={i}>{typeof e === 'string' ? e : extractText(e)}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(cds.exclusionsModified) && cds.exclusionsModified.length > 0 && (
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2 text-gray-900">Exclusions Modified</h3>
          <div className="space-y-2">
            {cds.exclusionsModified.map((m: any, i: number) => (
              <div key={i} className="border border-gray-200 rounded p-2 text-sm">
                <div className="font-medium text-gray-900">{m.exclusion}</div>
                {m.expiringLanguage && <div className="mt-1 text-xs"><span className="font-semibold">Expiring: </span>{extractText(m.expiringLanguage)}</div>}
                {m.renewalLanguage && <div className="mt-1 text-xs"><span className="font-semibold">Renewal: </span>{extractText(m.renewalLanguage)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {typeof cds.exclusionsModified === 'string' && matrixField('Exclusions Modified', cds.exclusionsModified)}
      {matrixField('Coverage Triggers', cds.coverageTriggers)}
      {Array.isArray(cds.keyDefinitions) && cds.keyDefinitions.length > 0 && (
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2 text-gray-900">Key Definitions</h3>
          <div className="space-y-2">
            {cds.keyDefinitions.map((kd: any, i: number) => (
              <div key={i} className={`border rounded p-2 text-sm ${kd.materialDifference ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                <div className="font-medium text-gray-900">
                  {kd.term}
                  {kd.materialDifference && <span className="ml-2 text-xs font-semibold text-amber-700">Material difference</span>}
                </div>
                {kd.expiringDefinition && <div className="mt-1 text-xs"><span className="font-semibold">Expiring: </span>{extractText(kd.expiringDefinition)}</div>}
                {kd.renewalDefinition && <div className="mt-1 text-xs"><span className="font-semibold">Renewal: </span>{extractText(kd.renewalDefinition)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {typeof cds.keyDefinitions === 'string' && matrixField('Key Definitions', cds.keyDefinitions)}
    </div>
  );
};

const ComplianceContractualSection: React.FC<{ data: any; contentWidth?: number }> = ({ data, contentWidth }) => {
  const cc = data?.reportAddOns?.complianceContractual;
  if (!cc) return null;
  return (
    <div className="space-y-3">
      {cc.summary && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded text-sm text-gray-800">
          {typeof cc.summary === 'string' ? cc.summary : extractText(cc.summary)}
        </div>
      )}
      {!cc.contractFound && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-900">
          No underlying contract was uploaded. Upload the relevant contract(s) for a full compliance review.
        </div>
      )}
      {(() => {
        const m = cc.coiRequirements;
        if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
          return (
            <div className="mb-3">
              <h3 className="text-base font-semibold mb-2 text-gray-900">COI Requirements vs Policy</h3>
              {renderAddOnMatrix(m.headers, m.rows, contentWidth)}
            </div>
          );
        }
        if (typeof m === 'string' && m.trim()) {
          return (
            <div className="mb-3">
              <h3 className="text-base font-semibold mb-2 text-gray-900">COI Requirements vs Policy</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{m}</p>
            </div>
          );
        }
        return null;
      })()}
      {(() => {
        const m = cc.contractualObligations;
        if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
          return (
            <div className="mb-3">
              <h3 className="text-base font-semibold mb-2 text-gray-900">Contractual Obligations</h3>
              {renderAddOnMatrix(m.headers, m.rows, contentWidth)}
            </div>
          );
        }
        if (typeof m === 'string' && m.trim()) {
          return (
            <div className="mb-3">
              <h3 className="text-base font-semibold mb-2 text-gray-900">Contractual Obligations</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{m}</p>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

// ============================================================================
// Per-item / per-block variants of the YOY add-on sections
// ============================================================================
// These render exactly ONE schedule (or ONE coverage block) so the V2
// paginator can split between schedules/blocks instead of mid-table-row.
// They share helpers (cellsRowToDict, renderAddOnMatrix, FitToWidth) with the
// "section per add-on" variants above.

const getScheduleList = (data: any): any[] => {
  const raw = data?.reportAddOns?.schedules;
  if (Array.isArray(raw)) {
    return raw.length > 0 && raw.every((s: any) => Array.isArray(s)) ? raw.flat() : raw;
  }
  if (raw && Array.isArray(raw.schedules)) return raw.schedules;
  return [];
};

const SingleScheduleSection: React.FC<{ data: any; scheduleIndex: number; contentWidth?: number }> = ({
  data,
  scheduleIndex,
  contentWidth,
}) => {
  const list = getScheduleList(data);
  const schedule = list[scheduleIndex];
  if (!schedule || !Array.isArray(schedule.headers) || !Array.isArray(schedule.rows)) return null;
  return (
    <div className="border border-gray-200 rounded">
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{schedule.scheduleType}</h3>
        {schedule.policyDocName && (
          <div className="text-xs text-gray-500 mt-0.5">Source: {schedule.policyDocName}</div>
        )}
      </div>
      <div className="p-2">
        {renderAddOnMatrix(schedule.headers, schedule.rows, contentWidth)}
        {Array.isArray(schedule.additionsVsPriorPolicy) && schedule.additionsVsPriorPolicy.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-semibold text-green-700 mb-1">Added vs prior policy</div>
            <ul className="list-disc list-inside text-sm text-gray-700">
              {schedule.additionsVsPriorPolicy.map((a: string, i: number) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(schedule.removalsVsPriorPolicy) && schedule.removalsVsPriorPolicy.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-semibold text-red-700 mb-1">Removed vs prior policy</div>
            <ul className="list-disc list-inside text-sm text-gray-700">
              {schedule.removalsVsPriorPolicy.map((r: string, i: number) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const CoverageDetailsBlockSection: React.FC<{ data: any; blockKey: string; contentWidth?: number }> = ({
  data,
  blockKey,
  contentWidth,
}) => {
  const cds = data?.reportAddOns?.coverageDetails;
  if (!cds) return null;

  // Combined Added / Removed sub-block
  if (blockKey === 'exclusionsAddedRemoved') {
    const added: any[] = Array.isArray(cds.exclusionsAdded) ? cds.exclusionsAdded : [];
    const removed: any[] = Array.isArray(cds.exclusionsRemoved) ? cds.exclusionsRemoved : [];
    if (added.length === 0 && removed.length === 0) return null;
    return (
      <div className="space-y-3">
        {added.length > 0 && (
          <div>
            <h3 className="text-base font-semibold mb-2 text-gray-900">Exclusions Added</h3>
            <ul className="list-disc list-inside text-sm text-gray-700">
              {added.map((e: any, i: number) => (
                <li key={i}>{typeof e === 'string' ? e : extractText(e)}</li>
              ))}
            </ul>
          </div>
        )}
        {removed.length > 0 && (
          <div>
            <h3 className="text-base font-semibold mb-2 text-gray-900">Exclusions Removed</h3>
            <ul className="list-disc list-inside text-sm text-gray-700">
              {removed.map((e: any, i: number) => (
                <li key={i}>{typeof e === 'string' ? e : extractText(e)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // exclusionsModified — array of {exclusion, expiringLanguage, renewalLanguage}
  if (blockKey === 'exclusionsModified') {
    const arr = cds.exclusionsModified;
    if (Array.isArray(arr) && arr.length > 0) {
      return (
        <div className="space-y-2">
          {arr.map((m: any, i: number) => (
            <div key={i} className="border border-gray-200 rounded p-2 text-sm">
              <div className="font-medium text-gray-900">{m.exclusion}</div>
              {m.expiringLanguage && <div className="mt-1 text-xs"><span className="font-semibold">Expiring: </span>{extractText(m.expiringLanguage)}</div>}
              {m.renewalLanguage && <div className="mt-1 text-xs"><span className="font-semibold">Renewal: </span>{extractText(m.renewalLanguage)}</div>}
            </div>
          ))}
        </div>
      );
    }
    if (typeof arr === 'string' && arr.trim()) {
      return <p className="text-sm text-gray-700 whitespace-pre-wrap">{arr}</p>;
    }
    return null;
  }

  // keyDefinitions — array of {term, expiringDefinition, renewalDefinition, materialDifference}
  if (blockKey === 'keyDefinitions') {
    const arr = cds.keyDefinitions;
    if (Array.isArray(arr) && arr.length > 0) {
      return (
        <div className="space-y-2">
          {arr.map((kd: any, i: number) => (
            <div key={i} className={`border rounded p-2 text-sm ${kd.materialDifference ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
              <div className="font-medium text-gray-900">
                {kd.term}
                {kd.materialDifference && <span className="ml-2 text-xs font-semibold text-amber-700">Material difference</span>}
              </div>
              {kd.expiringDefinition && <div className="mt-1 text-xs"><span className="font-semibold">Expiring: </span>{extractText(kd.expiringDefinition)}</div>}
              {kd.renewalDefinition && <div className="mt-1 text-xs"><span className="font-semibold">Renewal: </span>{extractText(kd.renewalDefinition)}</div>}
            </div>
          ))}
        </div>
      );
    }
    if (typeof arr === 'string' && arr.trim()) {
      return <p className="text-sm text-gray-700 whitespace-pre-wrap">{arr}</p>;
    }
    return null;
  }

  // Default: matrix-shaped block
  const m = cds[blockKey];
  if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
    return renderAddOnMatrix(m.headers, m.rows, contentWidth);
  }
  if (typeof m === 'string' && m.trim()) {
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{m}</p>;
  }
  return null;
};

const ComplianceBlockSection: React.FC<{ data: any; blockKey: string; contentWidth?: number }> = ({
  data,
  blockKey,
  contentWidth,
}) => {
  const cc = data?.reportAddOns?.complianceContractual;
  if (!cc) return null;

  if (blockKey === 'summary') {
    return (
      <div className="space-y-3">
        {cc.summary && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded text-sm text-gray-800">
            {typeof cc.summary === 'string' ? cc.summary : extractText(cc.summary)}
          </div>
        )}
        {!cc.contractFound && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-900">
            No underlying contract was uploaded. Upload the relevant contract(s) for a full compliance review.
          </div>
        )}
      </div>
    );
  }

  const m = cc[blockKey];
  if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
    return renderAddOnMatrix(m.headers, m.rows, contentWidth);
  }
  if (typeof m === 'string' && m.trim()) {
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{m}</p>;
  }
  return null;
};

// Disclaimer Section - always renders (uses default if none from AI)
const DisclaimerSection: React.FC<{ data: any } & SectionEditProps> = ({ data, isEditMode, editedContent, onContentChange }) => {
  const DEFAULT_DISCLAIMER = 'This analysis is generated by an AI assistant and is intended for use by experienced insurance professionals as a supplementary tool, not as a substitute for their own expert judgment. By using this analysis, you acknowledge and agree to the following limitations:\n\nNot Legal or Professional Advice: This analysis is a form of sophisticated pattern recognition and language processing; it is not legal advice, a definitive coverage determination, or a substitute for the nuanced judgment of a seasoned human professional.\n\nKnowledge Cutoff: The analysis may not reflect the most recent court decisions, regulatory changes, or emerging legal interpretations.\n\nContext is Key: This analysis is strictly limited to the documents and context provided.\n\nAll findings must be independently verified by a qualified professional.';
  const disclaimer = data?.disclaimer ||
    (typeof data?.professionalDisclaimer === 'string' ? data.professionalDisclaimer : data?.professionalDisclaimer?.text) ||
    DEFAULT_DISCLAIMER;

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <EditableText contentPath="disclaimer" originalValue={disclaimer} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="p" className="text-sm text-gray-600 italic" />
    </div>
  );
};

// === Insurance Requirements v2.0 Section Components ===

// Insurance Requirements - Executive Summary Section
const InsuranceExecutiveSummarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const summary = data?.executiveSummary;
  if (!summary) return null;

  return (
    <div className="insurance-executive-summary space-y-4">
      {/* Recommendation Badge */}
      {summary.recommendation && (
        <div className="mb-4">
          <span className={`inline-block px-4 py-2 rounded-lg text-lg font-bold ${
            summary.recommendation === 'APPROVE' ? 'bg-green-200 text-green-800' :
            summary.recommendation === 'DECLINE' ? 'bg-red-200 text-red-800' :
            'bg-yellow-200 text-yellow-800'
          }`}>
            {summary.recommendation}
          </span>
        </div>
      )}

      {/* Overall Status */}
      {(summary.overallStatus || summary.summary) && (
        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
          <p className="text-gray-700">
            {extractText(summary.overallStatus) || extractText(summary.summary)}
          </p>
        </div>
      )}

      {/* Recommendation Rationale */}
      {summary.recommendationRationale && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">Recommendation Rationale</h4>
          <p className="text-gray-700">{extractText(summary.recommendationRationale)}</p>
        </div>
      )}

      {/* Critical Deficiencies */}
      {summary.criticalDeficiencies && Array.isArray(summary.criticalDeficiencies) && summary.criticalDeficiencies.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-2">Critical Deficiencies</h4>
          <ul className="space-y-2">
            {summary.criticalDeficiencies.map((def: any, idx: number) => (
              <li key={idx} className="text-gray-700 flex items-start">
                <span className="text-red-500 mr-2">•</span>
                {extractText(def)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Client Renewal Presentation - Executive Summary Section
const ClientRenewalExecutiveSummarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const summary = data?.executiveSummary;
  if (!summary) return null;

  return (
    <div className="client-renewal-executive-summary space-y-4">
      {/* Main Summary Text */}
      {summary.text && (
        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
          <p className="text-gray-700 whitespace-pre-wrap">{extractText(summary.text)}</p>
        </div>
      )}

      {/* Key Highlights */}
      {summary.keyHighlights && (
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
          <h4 className="font-semibold text-emerald-800 mb-2">Key Highlights</h4>
          <p className="text-gray-700 whitespace-pre-wrap">{extractText(summary.keyHighlights)}</p>
        </div>
      )}

      {/* Overall Assessment */}
      {summary.overallAssessment && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h4 className="font-semibold text-amber-800 mb-2">Overall Assessment</h4>
          <p className="text-gray-700 whitespace-pre-wrap">{extractText(summary.overallAssessment)}</p>
        </div>
      )}

      {/* Critical Attention Items */}
      {summary.criticalAttentionItems && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-2">Critical Attention Items</h4>
          <p className="text-gray-700 whitespace-pre-wrap">{extractText(summary.criticalAttentionItems)}</p>
        </div>
      )}
    </div>
  );
};

// Insurance Requirements - Compliance Matrix Section
const ComplianceMatrixSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const matrix = data?.complianceMatrix;
  if (!matrix || !Array.isArray(matrix) || matrix.length === 0) return null;

  return (
    <div className="compliance-matrix space-y-4">
      {matrix.map((item: any, idx: number) => (
        <div key={idx} className={`p-4 rounded-lg border ${
          item.complianceStatus === 'PRECISELY MET' ? 'border-green-300 bg-green-50' :
          item.complianceStatus === 'NOT MET' ? 'border-red-300 bg-red-50' :
          'border-yellow-300 bg-yellow-50'
        }`}>
          <div className="flex items-start justify-between mb-2">
            <span className="font-bold text-gray-800">Requirement {item.requirementNumber}</span>
            <span className={`px-2 py-1 rounded text-sm font-medium ${
              item.complianceStatus === 'PRECISELY MET' ? 'bg-green-200 text-green-800' :
              item.complianceStatus === 'NOT MET' ? 'bg-red-200 text-red-800' :
              'bg-yellow-200 text-yellow-800'
            }`}>
              {item.complianceStatus}
            </span>
          </div>
          {item.requirement && (
            <p className="text-gray-700 mb-2 italic">"{item.requirement}"</p>
          )}
          {item.policyProvision && (
            <div className="text-sm text-gray-600 mb-2 bg-gray-100 p-2 rounded">
              <span className="font-medium">Policy Provision:</span> {item.policyProvision}
            </div>
          )}
          {item.rationale && (
            <p className="text-gray-700 mb-2">
              <span className="font-medium">Rationale:</span> {extractText(item.rationale)}
            </p>
          )}
          {item.actionRequired && item.actionRequired !== 'None' && (
            <p className="text-orange-700 font-medium">
              Action Required: {item.actionRequired}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

// Insurance Requirements - Interconnected Analysis Section
const InterconnectedAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const analysis = data?.interconnectedAnalysis;
  if (!analysis) return null;

  return (
    <div className="interconnected-analysis space-y-4">
      {/* Operating Model */}
      {analysis.operatingModelIdentified && (
        <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
          <h4 className="font-semibold text-purple-800 mb-2">Operating Model Identified</h4>
          <p className="text-gray-700">{extractText(analysis.operatingModelIdentified)}</p>
        </div>
      )}

      {/* Deficiency Synthesis */}
      {analysis.deficiencySynthesis && Array.isArray(analysis.deficiencySynthesis) && analysis.deficiencySynthesis.length > 0 && (
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <h4 className="font-semibold text-orange-800 mb-2">Deficiency Synthesis</h4>
          <ul className="space-y-2">
            {analysis.deficiencySynthesis.map((def: any, idx: number) => (
              <li key={idx} className="text-gray-700">{extractText(def)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk Escalations */}
      {analysis.riskEscalations && Array.isArray(analysis.riskEscalations) && analysis.riskEscalations.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-3">Risk Escalations</h4>
          {analysis.riskEscalations.map((esc: any, idx: number) => (
            <div key={idx} className="mb-3 p-3 bg-white rounded border border-red-100">
              <p className="font-medium text-gray-800 mb-1">{esc.deficiency}</p>
              <div className="text-sm">
                <span className="text-gray-600">Original Risk: </span>
                <span className="text-gray-800">{esc.originalRisk}</span>
                <span className="mx-2">→</span>
                <span className="text-red-600 font-medium">Compound Risk: {esc.compoundRisk}</span>
              </div>
              {esc.reasoning && (
                <p className="text-sm text-gray-600 mt-1">{esc.reasoning}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Coverage Philosophy Assessment */}
      {analysis.coveragePhilosophyAssessment && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">Coverage Philosophy Assessment</h4>
          <p className="text-gray-700">{extractText(analysis.coveragePhilosophyAssessment)}</p>
        </div>
      )}

      {/* Synthesis Conclusion */}
      {analysis.synthesisConclusion && (
        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
          <h4 className="font-semibold text-blue-800 mb-2">Synthesis Conclusion</h4>
          <p className="text-gray-700">{extractText(analysis.synthesisConclusion)}</p>
        </div>
      )}
    </div>
  );
};

// Insurance Requirements - Decision Pathway Section
const DecisionPathwaySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const pathway = data?.decisionPathway;
  if (!pathway) return null;

  return (
    <div className="decision-pathway space-y-4">
      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
        <div className="text-lg font-bold text-indigo-800 mb-2">
          {pathway.pathway} - {pathway.pathwayName}
        </div>
        {pathway.rationale && (
          <p className="text-gray-700 mb-3">{extractText(pathway.rationale)}</p>
        )}
        {pathway.specificActions && Array.isArray(pathway.specificActions) && pathway.specificActions.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Specific Actions</h4>
            <ol className="list-decimal list-inside space-y-1">
              {pathway.specificActions.map((action: any, idx: number) => (
                <li key={idx} className="text-gray-700">{extractText(action)}</li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

// Insurance Requirements - Blind Spot Acknowledgement Section
const BlindSpotAcknowledgementSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const blindSpot = data?.blindSpotAcknowledgement;
  if (!blindSpot) return null;

  return (
    <div className="blind-spot-acknowledgement space-y-4">
      {blindSpot.documentCompletenessAssessment && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h4 className="font-semibold text-amber-800 mb-2">Document Completeness</h4>
          <p className="text-gray-700">{extractText(blindSpot.documentCompletenessAssessment)}</p>
        </div>
      )}

      {blindSpot.limitationsDueToMissingInfo && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-2">Limitations</h4>
          <p className="text-gray-700">{extractText(blindSpot.limitationsDueToMissingInfo)}</p>
        </div>
      )}

      {blindSpot.missingDocuments && Array.isArray(blindSpot.missingDocuments) && blindSpot.missingDocuments.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-2">Missing Documents</h4>
          <ul className="list-disc list-inside space-y-1">
            {blindSpot.missingDocuments.map((doc: string, idx: number) => (
              <li key={idx} className="text-gray-700">{doc}</li>
            ))}
          </ul>
        </div>
      )}

      {blindSpot.caveat && (
        <p className="text-sm text-gray-600 italic">{blindSpot.caveat}</p>
      )}
    </div>
  );
};

// Insurance Requirements - Confidence Assessment Section
const ConfidenceAssessmentSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const assessment = data?.confidenceAssessment;
  if (!assessment) return null;

  return (
    <div className="confidence-assessment">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="text-2xl font-bold text-blue-800 mb-2">
          {assessment.percentage} Confidence
        </div>
        {assessment.sourceOfConfidence && (
          <p className="text-gray-700">{extractText(assessment.sourceOfConfidence)}</p>
        )}
      </div>
    </div>
  );
};

// Insurance Requirements - Professional Disclaimer Section
const ProfessionalDisclaimerSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const disclaimer = data?.professionalDisclaimer;
  if (!disclaimer) return null;

  return (
    <div className="professional-disclaimer">
      <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
        <p className="text-sm text-gray-600 italic">{extractText(disclaimer)}</p>
      </div>
    </div>
  );
};

// === Commercial Lines Executive Renewal Section Components ===

// Commercial Lines - Executive Renewal Summary Section
const ExecutiveRenewalSummarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const summary = data?.executiveRenewalSummary;
  if (!summary) return null;

  // Handle case where summary is a simple string
  if (typeof summary === 'string') {
    return (
      <div className="executive-renewal-summary">
        <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
          <p className="text-gray-700">{summary}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="executive-renewal-summary space-y-4">
      {/* Executive Summary of Changes */}
      {summary.executiveSummaryOfChanges && (
        <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
          <p className="text-gray-700">{extractText(summary.executiveSummaryOfChanges)}</p>
        </div>
      )}

      {/* Key Changes */}
      {summary.keyChanges && Array.isArray(summary.keyChanges) && summary.keyChanges.length > 0 && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-3">Key Changes</h4>
          <div className="space-y-3">
            {summary.keyChanges.map((change: any, idx: number) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded">
                <span className={`px-2 py-1 text-xs font-bold rounded ${
                  change.priority === 'High' ? 'bg-red-200 text-red-800' :
                  change.priority === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
                  'bg-green-200 text-green-800'
                }`}>{change.priority || 'Info'}</span>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{change.change}</p>
                  {change.impact && <p className="text-sm text-gray-600 mt-1">Impact: {change.impact}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Program Trajectory */}
      {summary.programTrajectory && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-2">Program Trajectory</h4>
          <p className="text-lg font-medium text-gray-800 mb-1">{summary.programTrajectory.assessment}</p>
          {summary.programTrajectory.rationale && (
            <p className="text-gray-700">{summary.programTrajectory.rationale}</p>
          )}
        </div>
      )}

      {/* Total Program Summary */}
      {summary.totalProgramSummary && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-3">Total Program Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {summary.totalProgramSummary.expiringTotalPremium && (
              <div>
                <p className="text-sm text-gray-600">Expiring Premium</p>
                <p className="font-bold text-gray-800">{summary.totalProgramSummary.expiringTotalPremium}</p>
              </div>
            )}
            {summary.totalProgramSummary.renewalTotalPremium && (
              <div>
                <p className="text-sm text-gray-600">Renewal Premium</p>
                <p className="font-bold text-gray-800">{summary.totalProgramSummary.renewalTotalPremium}</p>
              </div>
            )}
            {summary.totalProgramSummary.totalChange && (
              <div>
                <p className="text-sm text-gray-600">Total Change</p>
                <p className={`font-bold ${summary.totalProgramSummary.totalChange.startsWith('-') ? 'text-green-600' : 'text-red-600'}`}>
                  {summary.totalProgramSummary.totalChange}
                </p>
              </div>
            )}
            {summary.totalProgramSummary.totalChangePercent && (
              <div>
                <p className="text-sm text-gray-600">Change %</p>
                <p className={`font-bold ${summary.totalProgramSummary.totalChangePercent.startsWith('-') ? 'text-green-600' : 'text-red-600'}`}>
                  {summary.totalProgramSummary.totalChangePercent}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Commercial Lines - Renewal Program Matrix Section
const RenewalProgramMatrixSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const matrix = data?.executiveRenewalSummary?.renewalProgramMatrix;
  if (!matrix || !Array.isArray(matrix) || matrix.length === 0) return null;

  return (
    <div className="renewal-program-matrix overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-100">
          <tr>
            <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Coverage Line</th>
            <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Carrier</th>
            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold">Expiring Limit</th>
            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold">Renewal Limit</th>
            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold">Expiring Premium</th>
            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold">Renewal Premium</th>
            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold">Change</th>
          </tr>
        </thead>
        <tbody>
          {matrix.map((row: any, idx: number) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border border-gray-300 px-3 py-2 text-sm font-medium">{row.coverageLine}</td>
              <td className="border border-gray-300 px-3 py-2 text-sm">{row.carrier}</td>
              <td className="border border-gray-300 px-3 py-2 text-sm text-right">{row.expiringLimit || '-'}</td>
              <td className="border border-gray-300 px-3 py-2 text-sm text-right">{row.renewalLimit || '-'}</td>
              <td className="border border-gray-300 px-3 py-2 text-sm text-right">{row.expiringPremium || '-'}</td>
              <td className="border border-gray-300 px-3 py-2 text-sm text-right">{row.renewalPremium || '-'}</td>
              <td className={`border border-gray-300 px-3 py-2 text-sm text-right font-medium ${
                row.premiumChangePercent?.startsWith('-') ? 'text-green-600' :
                row.premiumChangePercent?.startsWith('+') || parseFloat(row.premiumChangePercent) > 0 ? 'text-red-600' : ''
              }`}>
                {row.premiumChangePercent || row.premiumChange || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Commercial Lines - Entity Overview Section
const EntityOverviewSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const overview = data?.riskIntelligenceReport?.entityOverview;
  if (!overview) return null;

  return (
    <div className="entity-overview">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {overview.entityName && (
            <div>
              <p className="text-sm text-gray-600">Entity Name</p>
              <p className="font-bold text-gray-800">{overview.entityName}</p>
            </div>
          )}
          {overview.industry && (
            <div>
              <p className="text-sm text-gray-600">Industry</p>
              <p className="font-medium text-gray-800">{overview.industry}</p>
            </div>
          )}
          {overview.revenue && (
            <div>
              <p className="text-sm text-gray-600">Revenue</p>
              <p className="font-medium text-gray-800">{overview.revenue}</p>
            </div>
          )}
          {overview.employeeCount && (
            <div>
              <p className="text-sm text-gray-600">Employees</p>
              <p className="font-medium text-gray-800">{overview.employeeCount}</p>
            </div>
          )}
          {overview.operatingJurisdictions && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">Operating Jurisdictions</p>
              <p className="font-medium text-gray-800">{overview.operatingJurisdictions}</p>
            </div>
          )}
          {overview.businessDescription && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">Business Description</p>
              <p className="text-gray-700">{overview.businessDescription}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Commercial Lines - Research Methodology Section
const ResearchMethodologySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const methodology = data?.riskIntelligenceReport?.researchMethodology;
  if (!methodology) return null;

  return (
    <div className="research-methodology">
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {methodology.insuranceDocumentsAnalyzed && (
            <div><span className="text-gray-600">Documents Analyzed:</span> <span className="font-medium">{methodology.insuranceDocumentsAnalyzed}</span></div>
          )}
          {methodology.entityResearchSources && (
            <div><span className="text-gray-600">Research Sources:</span> <span className="font-medium">{methodology.entityResearchSources}</span></div>
          )}
          {methodology.riskDomainsAssessed && (
            <div><span className="text-gray-600">Risk Domains:</span> <span className="font-medium">{methodology.riskDomainsAssessed}</span></div>
          )}
          {methodology.researchDate && (
            <div><span className="text-gray-600">Research Date:</span> <span className="font-medium">{methodology.researchDate}</span></div>
          )}
          {methodology.researchConfidence && (
            <div><span className="text-gray-600">Confidence:</span> <span className="font-medium">{methodology.researchConfidence}</span></div>
          )}
        </div>
      </div>
    </div>
  );
};

// Commercial Lines - Critical Path Priorities Section
const CriticalPathPrioritiesSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const priorities = data?.riskIntelligenceReport?.criticalPathPriorities;
  if (!priorities || !Array.isArray(priorities) || priorities.length === 0) return null;

  return (
    <div className="critical-path-priorities space-y-3">
      {priorities.map((priority: any, idx: number) => (
        <div key={idx} className={`p-4 rounded-lg border-l-4 ${
          priority.priorityLevel === 'Critical' ? 'border-red-500 bg-red-50' :
          priority.priorityLevel === 'High' ? 'border-orange-500 bg-orange-50' :
          priority.priorityLevel === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
          'border-blue-500 bg-blue-50'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl font-bold text-gray-400">#{priority.priorityRank}</span>
            <div>
              <span className="font-bold text-gray-800">{priority.domain}</span>
              {priority.priorityScore && <span className="ml-2 text-sm text-gray-600">Score: {priority.priorityScore}</span>}
            </div>
          </div>
          {priority.whyCritical && <p className="text-gray-700 mb-2">{priority.whyCritical}</p>}
          {priority.immediateAction && (
            <p className="text-sm font-medium text-gray-800 bg-white p-2 rounded">
              <span className="text-gray-600">Action:</span> {priority.immediateAction}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

// Commercial Lines - Comprehensive Risk Assessment Section
const ComprehensiveRiskAssessmentSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const assessment = data?.riskIntelligenceReport?.comprehensiveRiskAssessment;
  if (!assessment || !Array.isArray(assessment) || assessment.length === 0) return null;

  return (
    <div className="comprehensive-risk-assessment space-y-3">
      {assessment.map((risk: any, idx: number) => (
        <div key={idx} className={`p-4 rounded-lg border ${
          risk.inherentRiskLevel === 'Critical' || risk.inherentRiskLevel === 'Very High' ? 'border-red-300 bg-red-50' :
          risk.inherentRiskLevel === 'High' ? 'border-orange-300 bg-orange-50' :
          risk.inherentRiskLevel === 'Medium' ? 'border-yellow-300 bg-yellow-50' :
          'border-green-300 bg-green-50'
        }`}>
          <div className="flex items-start justify-between mb-2">
            <span className="font-bold text-gray-800">{risk.domain}</span>
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              risk.inherentRiskLevel === 'Critical' || risk.inherentRiskLevel === 'Very High' ? 'bg-red-200 text-red-800' :
              risk.inherentRiskLevel === 'High' ? 'bg-orange-200 text-orange-800' :
              risk.inherentRiskLevel === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
              'bg-green-200 text-green-800'
            }`}>{risk.inherentRiskLevel}</span>
          </div>
          {risk.rationale && <p className="text-gray-700 text-sm mb-2">{risk.rationale}</p>}
          <div className="flex flex-wrap gap-2 text-xs">
            {risk.velocity && <span className="bg-gray-200 px-2 py-1 rounded">Velocity: {risk.velocity}</span>}
            {risk.confidence && <span className="bg-gray-200 px-2 py-1 rounded">Confidence: {risk.confidence}</span>}
            {risk.connectedRisks && <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">Connected: {risk.connectedRisks}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Commercial Lines - Coverage Applicability Analysis Section
const CoverageApplicabilityAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const analysis = data?.riskIntelligenceReport?.coverageApplicabilityAnalysis;
  if (!analysis) return null;

  return (
    <div className="coverage-applicability-analysis space-y-4">
      {/* Risks Adequately Addressed */}
      {analysis.risksAdequatelyAddressed && Array.isArray(analysis.risksAdequatelyAddressed) && analysis.risksAdequatelyAddressed.length > 0 && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-3">Risks Adequately Addressed</h4>
          <div className="space-y-2">
            {analysis.risksAdequatelyAddressed.map((risk: any, idx: number) => (
              <div key={idx} className="p-2 bg-white rounded border border-green-100">
                <p className="font-medium text-gray-800">{risk.riskDomain}</p>
                <p className="text-sm text-gray-600">Covered by: {risk.coveredBy}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risks Partially Covered */}
      {analysis.risksPartiallyCovered && Array.isArray(analysis.risksPartiallyCovered) && analysis.risksPartiallyCovered.length > 0 && (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h4 className="font-semibold text-yellow-800 mb-3">Risks Partially Covered</h4>
          <div className="space-y-2">
            {analysis.risksPartiallyCovered.map((risk: any, idx: number) => (
              <div key={idx} className="p-2 bg-white rounded border border-yellow-100">
                <p className="font-medium text-gray-800">{risk.riskDomain}</p>
                {risk.limitation && <p className="text-sm text-gray-600">Limitation: {risk.limitation}</p>}
                {risk.recommendation && <p className="text-sm text-yellow-700 font-medium">Recommendation: {risk.recommendation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coverage Gaps */}
      {analysis.coverageGapsIdentified && Array.isArray(analysis.coverageGapsIdentified) && analysis.coverageGapsIdentified.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-3">Coverage Gaps Identified</h4>
          <div className="space-y-2">
            {analysis.coverageGapsIdentified.map((gap: any, idx: number) => (
              <div key={idx} className="p-3 bg-white rounded border border-red-100">
                <div className="flex items-start justify-between mb-2">
                  <p className="font-medium text-gray-800">{gap.riskDomain}</p>
                  <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">{gap.gapType}</span>
                </div>
                {gap.whyCritical && <p className="text-sm text-gray-700 mb-2">{gap.whyCritical}</p>}
                {gap.recommendedApproach && <p className="text-sm text-green-700 font-medium">Approach: {gap.recommendedApproach}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Commercial Lines - Risk Mitigation Framework Section
const RiskMitigationFrameworkSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const framework = data?.riskIntelligenceReport?.riskMitigationFramework;
  if (!framework || !Array.isArray(framework) || framework.length === 0) return null;

  return (
    <div className="risk-mitigation-framework space-y-3">
      {framework.map((item: any, idx: number) => (
        <div key={idx} className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-gray-800">{item.topic}</span>
            {item.priority && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                item.priority === 'High' ? 'bg-red-100 text-red-800' :
                item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>{item.priority}</span>
            )}
          </div>
          {item.talkingPoints && <p className="text-gray-700 text-sm mb-2">{item.talkingPoints}</p>}
          {item.recommendedActions && (
            <p className="text-sm bg-blue-50 p-2 rounded">
              <span className="font-medium text-blue-800">Actions:</span> {item.recommendedActions}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

// Commercial Lines - Risk Correlation Map Section
const RiskCorrelationMapSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const correlations = data?.riskIntelligenceReport?.riskCorrelationMap;
  if (!correlations || !Array.isArray(correlations) || correlations.length === 0) return null;

  return (
    <div className="risk-correlation-map space-y-3">
      {correlations.map((scenario: any, idx: number) => (
        <div key={idx} className="p-4 rounded-lg border border-purple-200 bg-purple-50">
          <h4 className="font-bold text-purple-800 mb-2">{scenario.scenario}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-600">Primary Risk:</span> <span className="font-medium">{scenario.primaryRisk}</span></div>
            {scenario.connectedRisks && <div><span className="text-gray-600">Connected:</span> <span className="font-medium">{scenario.connectedRisks}</span></div>}
            {scenario.trigger && <div><span className="text-gray-600">Trigger:</span> <span className="font-medium">{scenario.trigger}</span></div>}
            {scenario.cascade && <div className="md:col-span-2"><span className="text-gray-600">Cascade:</span> <span className="font-medium">{scenario.cascade}</span></div>}
            {scenario.cumulativeImpact && <div className="md:col-span-2 text-red-700"><span className="font-medium">Cumulative Impact:</span> {scenario.cumulativeImpact}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Commercial Lines - Confidence & Limitations Section
const ConfidenceAndLimitationsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const conf = data?.confidenceAndLimitations;
  if (!conf) return null;

  // Handle case where conf is a simple string (like "90%")
  if (typeof conf === 'string') {
    return (
      <div className="confidence-and-limitations">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-2xl font-bold text-blue-800">{conf} Confidence</p>
        </div>
      </div>
    );
  }

  return (
    <div className="confidence-and-limitations space-y-4">
      {conf.overallConfidence && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-2xl font-bold text-blue-800">{conf.overallConfidence} Overall Confidence</p>
        </div>
      )}

      {conf.limitations && Array.isArray(conf.limitations) && conf.limitations.length > 0 && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h4 className="font-semibold text-amber-800 mb-2">Limitations</h4>
          <ul className="list-disc list-inside space-y-1">
            {conf.limitations.map((limitation: string, idx: number) => (
              <li key={idx} className="text-gray-700 text-sm">{limitation}</li>
            ))}
          </ul>
        </div>
      )}

      {conf.recommendationsForImprovement && Array.isArray(conf.recommendationsForImprovement) && conf.recommendationsForImprovement.length > 0 && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-2">Recommendations for Improvement</h4>
          <ul className="list-disc list-inside space-y-1">
            {conf.recommendationsForImprovement.map((rec: string, idx: number) => (
              <li key={idx} className="text-gray-700 text-sm">{rec}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// === Financial Analysis Section Components ===

// Financial Risk Assessment Section
const FinancialRiskAssessmentSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const assessment = data?.financialRiskAssessment;
  if (!assessment) return null;

  const getRiskColor = (score: number | string) => {
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    if (numScore >= 8) return 'text-red-600 bg-red-50';
    if (numScore >= 6) return 'text-amber-600 bg-amber-50';
    if (numScore >= 4) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getCategoryColor = (category: string) => {
    const lower = category?.toLowerCase() || '';
    if (lower.includes('high') || lower.includes('severe')) return 'text-red-700 bg-red-100';
    if (lower.includes('moderate') || lower.includes('medium')) return 'text-amber-700 bg-amber-100';
    if (lower.includes('low') || lower.includes('minimal')) return 'text-green-700 bg-green-100';
    return 'text-gray-700 bg-gray-100';
  };

  return (
    <div className="financial-risk-assessment space-y-4">
      {/* Overall Score */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-semibold">Overall Risk Score</span>
          <span className={`text-3xl font-bold px-4 py-2 rounded ${getRiskColor(assessment.overallScore)}`}>
            {assessment.overallScore}/10
          </span>
        </div>
        {assessment.riskCategory && (
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(assessment.riskCategory)}`}>
            {assessment.riskCategory}
          </span>
        )}
        {assessment.riskTrajectory && (
          <p className="text-gray-600 mt-2 text-sm">Trajectory: {extractText(assessment.riskTrajectory)}</p>
        )}
      </div>

      {/* Risk Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {assessment.liquidityRisk && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">Liquidity Risk</h4>
            <div className={`text-2xl font-bold mb-1 ${getRiskColor(assessment.liquidityRisk.score)}`}>
              {assessment.liquidityRisk.score}/10
            </div>
            <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(assessment.liquidityRisk.category)}`}>
              {assessment.liquidityRisk.category}
            </span>
          </div>
        )}
        {assessment.profitabilityRisk && (
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800 mb-2">Profitability Risk</h4>
            <div className={`text-2xl font-bold mb-1 ${getRiskColor(assessment.profitabilityRisk.score)}`}>
              {assessment.profitabilityRisk.score}/10
            </div>
            <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(assessment.profitabilityRisk.category)}`}>
              {assessment.profitabilityRisk.category}
            </span>
          </div>
        )}
        {assessment.leverageRisk && (
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h4 className="font-semibold text-orange-800 mb-2">Leverage Risk</h4>
            <div className={`text-2xl font-bold mb-1 ${getRiskColor(assessment.leverageRisk.score)}`}>
              {assessment.leverageRisk.score}/10
            </div>
            <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(assessment.leverageRisk.category)}`}>
              {assessment.leverageRisk.category}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Detailed Financial Analysis Section
const DetailedFinancialAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const analysis = data?.detailedAnalysis;
  if (!analysis) return null;

  const sections = [
    { key: 'revenueAnalysis', title: 'Revenue Analysis', icon: '📈' },
    { key: 'profitabilityAnalysis', title: 'Profitability Analysis', icon: '💰' },
    { key: 'liquidityAnalysis', title: 'Liquidity Analysis', icon: '💧' },
    { key: 'leverageAnalysis', title: 'Leverage Analysis', icon: '⚖️' },
    { key: 'cashFlowAnalysis', title: 'Cash Flow Analysis', icon: '💵' },
    { key: 'industrySpecificAnalysis', title: 'Industry-Specific Analysis', icon: '🏭' },
    { key: 'footnoteAnalysis', title: 'Footnote Analysis', icon: '📝' },
  ];

  return (
    <div className="detailed-financial-analysis space-y-4">
      {sections.map(({ key, title, icon }) => {
        const content = analysis[key];
        if (!content) return null;
        return (
          <div key={key} className="bg-gray-50 p-4 rounded-lg border">
            <h4 className="font-semibold text-gray-800 mb-2">{icon} {title}</h4>
            <div className="text-gray-700 text-sm whitespace-pre-wrap">{extractText(content)}</div>
          </div>
        );
      })}
    </div>
  );
};

// Period Comparison Section
const PeriodComparisonSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const comparison = data?.periodComparison;
  if (!comparison) return null;

  // periodComparison is an object with periodsAnalyzed and various metric arrays
  const metricTypes = [
    { key: 'revenueMetrics', title: 'Revenue Metrics' },
    { key: 'profitabilityMetrics', title: 'Profitability Metrics' },
    { key: 'liquidityMetrics', title: 'Liquidity Metrics' },
    { key: 'leverageMetrics', title: 'Leverage Metrics' },
  ];

  const periods = comparison.periodsAnalyzed || [];

  return (
    <div className="period-comparison space-y-6">
      {/* Periods Analyzed */}
      {periods.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {periods.map((period: string, idx: number) => (
            <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {period}
            </span>
          ))}
        </div>
      )}

      {/* Metric Tables */}
      {metricTypes.map(({ key, title }) => {
        const metrics = comparison[key];
        if (!metrics || !Array.isArray(metrics) || metrics.length === 0) return null;

        return (
          <div key={key} className="mb-4">
            <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Metric</th>
                    {periods.map((period: string, idx: number) => (
                      <th key={idx} className="border border-gray-300 px-3 py-2 text-right font-semibold">{period}</th>
                    ))}
                    <th className="border border-gray-300 px-3 py-2 text-right font-semibold">Change</th>
                    <th className="border border-gray-300 px-3 py-2 text-center font-semibold">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((row: any, idx: number) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-3 py-2 font-medium">{row.metric}</td>
                      {row.values?.map((val: string, vIdx: number) => (
                        <td key={vIdx} className="border border-gray-300 px-3 py-2 text-right">{val}</td>
                      ))}
                      <td className={`border border-gray-300 px-3 py-2 text-right font-medium ${
                        row.percentChange?.includes('+') ? 'text-green-600' :
                        row.percentChange?.includes('-') ? 'text-red-600' : ''
                      }`}>
                        {row.percentChange || '-'}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.trend?.toLowerCase().includes('increas') ? 'bg-green-100 text-green-800' :
                          row.trend?.toLowerCase().includes('decreas') ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {row.trend || 'Stable'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ERM Risk Mapping Section
const ErmRiskMappingSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const mapping = data?.ermRiskMapping;
  if (!mapping) return null;

  const riskCategories = [
    { key: 'strategicRisks', title: 'Strategic Risks', color: 'purple', icon: '🎯' },
    { key: 'operationalRisks', title: 'Operational Risks', color: 'blue', icon: '⚙️' },
    { key: 'financialRisks', title: 'Financial Risks', color: 'green', icon: '💵' },
    { key: 'complianceRisks', title: 'Compliance Risks', color: 'amber', icon: '📜' },
  ];

  return (
    <div className="erm-risk-mapping grid grid-cols-1 md:grid-cols-2 gap-4">
      {riskCategories.map(({ key, title, color, icon }) => {
        const risks = mapping[key];
        if (!risks || !Array.isArray(risks) || risks.length === 0) return null;
        return (
          <div key={key} className={`bg-${color}-50 p-4 rounded-lg border border-${color}-200`}>
            <h4 className={`font-semibold text-${color}-800 mb-3`}>{icon} {title}</h4>
            <div className="space-y-2">
              {risks.map((risk: any, idx: number) => (
                <div key={idx} className="bg-white p-2 rounded border">
                  <div className="font-medium text-gray-800 text-sm">{extractText(risk.risk)}</div>
                  <div className="text-gray-600 text-xs mt-1">Indicator: {extractText(risk.indicator)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Underwriting Recommendation Section
const UnderwritingRecommendationSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const rec = data?.underwritingRecommendation;
  if (!rec) return null;

  const getDecisionColor = (decision: string) => {
    const lower = decision?.toLowerCase() || '';
    if (lower.includes('decline') || lower.includes('reject')) return 'bg-red-100 text-red-800 border-red-300';
    if (lower.includes('conditional') || lower.includes('caution')) return 'bg-amber-100 text-amber-800 border-amber-300';
    if (lower.includes('accept') || lower.includes('approve')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="underwriting-recommendation space-y-4">
      {/* Decision */}
      <div className={`p-4 rounded-lg border-2 ${getDecisionColor(rec.decision)}`}>
        <div className="text-xl font-bold mb-2">Decision: {extractText(rec.decision)}</div>
        {rec.rationale && (
          <p className="text-sm">{extractText(rec.rationale)}</p>
        )}
      </div>

      {/* Conditions */}
      {rec.conditions && Array.isArray(rec.conditions) && rec.conditions.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-2">Conditions</h4>
          <ul className="list-disc list-inside space-y-1">
            {rec.conditions.map((condition: string, idx: number) => (
              <li key={idx} className="text-gray-700 text-sm">{extractText(condition)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Monitoring Requirements */}
      {rec.monitoringRequirements && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-semibold text-gray-800 mb-2">Monitoring Requirements</h4>
          <p className="text-gray-700 text-sm">{extractText(rec.monitoringRequirements)}</p>
        </div>
      )}
    </div>
  );
};

// === Client Renewal Presentation Section Components ===

// Company Context Section
const CompanyContextSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const context = data?.companyContext;
  if (!context) return null;

  return (
    <div className="company-context space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {context.entityName && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-600">Entity Name</div>
            <div className="text-lg font-bold text-blue-800">{extractText(context.entityName)}</div>
          </div>
        )}
        {context.industrySector && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="text-sm text-gray-600">Industry Sector</div>
            <div className="font-semibold text-gray-800">{extractText(context.industrySector)}</div>
          </div>
        )}
      </div>
      {context.primaryBusiness && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-semibold text-gray-800 mb-2">Primary Business</h4>
          <p className="text-gray-700 text-sm">{extractText(context.primaryBusiness)}</p>
        </div>
      )}
      {context.keyOperations && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-semibold text-gray-800 mb-2">Key Operations</h4>
          <p className="text-gray-700 text-sm">{extractText(context.keyOperations)}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {context.clientBase && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="text-sm text-gray-600">Client Base</div>
            <div className="text-gray-800">{extractText(context.clientBase)}</div>
          </div>
        )}
        {context.geographicFootprint && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="text-sm text-gray-600">Geographic Footprint</div>
            <div className="text-gray-800">{extractText(context.geographicFootprint)}</div>
          </div>
        )}
        {context.scaleIndicators && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="text-sm text-gray-600">Scale Indicators</div>
            <div className="text-gray-800">{extractText(context.scaleIndicators)}</div>
          </div>
        )}
        {context.riskManagementSophistication && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="text-sm text-gray-600">Risk Management Sophistication</div>
            <div className="text-gray-800">{extractText(context.riskManagementSophistication)}</div>
          </div>
        )}
      </div>
      {context.likelyAudience && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h4 className="font-semibold text-amber-800 mb-2">Likely Audience</h4>
          <p className="text-amber-700 text-sm">{extractText(context.likelyAudience)}</p>
        </div>
      )}
    </div>
  );
};

// Document Inventory Section
const DocumentInventorySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const inventory = data?.documentInventory;
  if (!inventory || !Array.isArray(inventory)) return null;

  return (
    <div className="document-inventory">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Document Type</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Policy Line</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Carrier</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Completeness</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {inventory.map((doc: any, idx: number) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm text-gray-800">{extractText(doc.documentType)}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{extractText(doc.policyLine)}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{extractText(doc.carrier) || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{extractText(doc.policyPeriod) || '-'}</td>
                <td className="px-4 py-2 text-sm">
                  <span className={`px-2 py-1 rounded text-xs ${
                    doc.completenessLevel?.toLowerCase().includes('complete') ? 'bg-green-100 text-green-800' :
                    doc.completenessLevel?.toLowerCase().includes('partial') ? 'bg-amber-100 text-amber-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {extractText(doc.completenessLevel) || '-'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Program Overview Section
const ProgramOverviewSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const overview = data?.programOverview;
  if (!overview) return null;

  return (
    <div className="program-overview space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {overview.totalPolicies && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-600">Total Policies</div>
            <div className="text-lg font-bold text-blue-800">{extractText(overview.totalPolicies)}</div>
          </div>
        )}
        {overview.totalPremium && (
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <div className="text-sm text-green-600">Total Premium</div>
            <div className="text-lg font-bold text-green-800">{extractText(overview.totalPremium)}</div>
          </div>
        )}
        {overview.premiumChange && (
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <div className="text-sm text-amber-600">Premium Change</div>
            <div className="text-lg font-bold text-amber-800">{extractText(overview.premiumChange)}</div>
          </div>
        )}
      </div>
      {overview.overallAssessment && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-semibold text-gray-800 mb-2">Overall Assessment</h4>
          <p className="text-gray-700 text-sm">{extractText(overview.overallAssessment)}</p>
        </div>
      )}
    </div>
  );
};

// Coverage Highlights Section
const CoverageHighlightsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const highlights = data?.coverageHighlights;
  if (!highlights || !Array.isArray(highlights)) return null;

  return (
    <div className="coverage-highlights space-y-4">
      {highlights.map((coverage: any, idx: number) => (
        <div key={idx} className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-bold text-gray-800">{extractText(coverage.coverageName)}</h4>
            {coverage.changeAssessment && (
              <span className={`text-xs px-2 py-1 rounded ${
                coverage.changeAssessment?.toLowerCase().includes('better') ? 'bg-green-100 text-green-800' :
                coverage.changeAssessment?.toLowerCase().includes('worse') ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {extractText(coverage.changeAssessment)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm mb-2">
            {coverage.carrier && <div><span className="text-gray-500">Carrier:</span> {extractText(coverage.carrier)}</div>}
            {coverage.limits && <div><span className="text-gray-500">Limits:</span> {extractText(coverage.limits)}</div>}
            {coverage.deductible && <div><span className="text-gray-500">Deductible:</span> {extractText(coverage.deductible)}</div>}
            {coverage.premium && <div><span className="text-gray-500">Premium:</span> {extractText(coverage.premium)}</div>}
          </div>
          {coverage.whatItProtects && (
            <p className="text-gray-600 text-sm">{extractText(coverage.whatItProtects)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// Restrictive Endorsements Section
const RestrictiveEndorsementsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const endorsements = data?.restrictiveEndorsements;
  if (!endorsements) return null;

  return (
    <div className="restrictive-endorsements space-y-4">
      {endorsements.subcontractorWarningPresent && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <span className="text-red-500 font-bold mr-2">⚠️</span>
            <span className="font-bold text-red-800">Subcontractor Warning Present</span>
          </div>
          {endorsements.subcontractorWarningDetails && (
            <p className="text-red-700 text-sm mt-1">{extractText(endorsements.subcontractorWarningDetails)}</p>
          )}
        </div>
      )}

      {endorsements.criticalRestrictions && Array.isArray(endorsements.criticalRestrictions) && endorsements.criticalRestrictions.length > 0 && (
        <div>
          <h4 className="font-semibold text-red-800 mb-2">Critical Restrictions</h4>
          <div className="space-y-2">
            {endorsements.criticalRestrictions.map((restriction: any, idx: number) => (
              <div key={idx} className="bg-red-50 p-3 rounded border border-red-200">
                <div className="font-medium text-red-800">{extractText(restriction.restrictionName)}</div>
                <p className="text-red-700 text-sm mt-1">{extractText(restriction.whatIsNotCovered)}</p>
                {restriction.businessImpact && (
                  <p className="text-gray-600 text-xs mt-1">Impact: {extractText(restriction.businessImpact)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {endorsements.importantRestrictions && Array.isArray(endorsements.importantRestrictions) && endorsements.importantRestrictions.length > 0 && (
        <div>
          <h4 className="font-semibold text-amber-800 mb-2">Important Restrictions</h4>
          <div className="space-y-2">
            {endorsements.importantRestrictions.map((restriction: any, idx: number) => (
              <div key={idx} className="bg-amber-50 p-3 rounded border border-amber-200">
                <div className="font-medium text-amber-800">{extractText(restriction.restrictionName)}</div>
                <p className="text-amber-700 text-sm mt-1">{extractText(restriction.whatIsNotCovered)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {endorsements.positiveEndorsements && Array.isArray(endorsements.positiveEndorsements) && endorsements.positiveEndorsements.length > 0 && (
        <div>
          <h4 className="font-semibold text-green-800 mb-2">Positive Endorsements</h4>
          <div className="space-y-2">
            {endorsements.positiveEndorsements.map((endorsement: any, idx: number) => (
              <div key={idx} className="bg-green-50 p-3 rounded border border-green-200">
                <div className="font-medium text-green-800">{extractText(endorsement.endorsementName)}</div>
                <p className="text-green-700 text-sm mt-1">{extractText(endorsement.whatItAdds)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Premium Analysis Section
const PremiumAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const analysis = data?.premiumAnalysis;
  if (!analysis) return null;

  return (
    <div className="premium-analysis space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analysis.totalExpiringPremium && (
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="text-sm text-gray-600">Expiring Premium</div>
            <div className="text-lg font-bold text-gray-800">{extractText(analysis.totalExpiringPremium)}</div>
          </div>
        )}
        {analysis.totalRenewalPremium && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-600">Renewal Premium</div>
            <div className="text-lg font-bold text-blue-800">{extractText(analysis.totalRenewalPremium)}</div>
          </div>
        )}
        {analysis.totalChange && (
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <div className="text-sm text-amber-600">Total Change</div>
            <div className="text-lg font-bold text-amber-800">{extractText(analysis.totalChange)}</div>
          </div>
        )}
        {analysis.totalChangePercent && (
          <div className={`p-3 rounded-lg border ${
            analysis.totalChangePercent?.includes('-') ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="text-sm text-gray-600">Change %</div>
            <div className="text-lg font-bold">{extractText(analysis.totalChangePercent)}</div>
          </div>
        )}
      </div>

      {analysis.primaryDrivers && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-semibold text-gray-800 mb-2">Primary Drivers</h4>
          <p className="text-gray-700 text-sm">{extractText(analysis.primaryDrivers)}</p>
        </div>
      )}

      {analysis.policyBreakdown && Array.isArray(analysis.policyBreakdown) && analysis.policyBreakdown.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">Policy Breakdown</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-3 py-2 text-left">Coverage</th>
                  <th className="border px-3 py-2 text-left">Expiring</th>
                  <th className="border px-3 py-2 text-left">Renewal</th>
                  <th className="border px-3 py-2 text-left">Change</th>
                </tr>
              </thead>
              <tbody>
                {analysis.policyBreakdown.map((policy: any, idx: number) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border px-3 py-2 font-medium">{extractText(policy.coverageName)}</td>
                    <td className="border px-3 py-2">{extractText(policy.expiringPremium)}</td>
                    <td className="border px-3 py-2">{extractText(policy.renewalPremium)}</td>
                    <td className="border px-3 py-2">{extractText(policy.change) || extractText(policy.changePercent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Coverage Changes Section
const CoverageChangesSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const changes = data?.coverageChanges;
  if (!changes || !Array.isArray(changes)) return null;

  return (
    <div className="coverage-changes space-y-3">
      {changes.map((change: any, idx: number) => (
        <div key={idx} className={`p-4 rounded-lg border ${
          change.assessment?.toLowerCase().includes('better') ? 'bg-green-50 border-green-200' :
          change.assessment?.toLowerCase().includes('worse') ? 'bg-red-50 border-red-200' :
          'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex justify-between items-start mb-2">
            <span className="font-bold text-gray-800">{extractText(change.coverageName)}</span>
            <span className="text-xs px-2 py-1 rounded bg-white">{extractText(change.changeType)}</span>
          </div>
          <p className="text-gray-700 text-sm mb-2">{extractText(change.changeDescription)}</p>
          <div className="flex justify-between text-sm">
            {change.expiringValue && <span>Expiring: {extractText(change.expiringValue)}</span>}
            {change.renewalValue && <span>Renewal: {extractText(change.renewalValue)}</span>}
          </div>
          {change.clientImpact && (
            <p className="text-gray-600 text-xs mt-2">Impact: {extractText(change.clientImpact)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// Red Flags Section
const RedFlagsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const flags = data?.redFlags;
  if (!flags || !Array.isArray(flags)) return null;

  return (
    <div className="red-flags space-y-3">
      {flags.map((flag: any, idx: number) => (
        <div key={idx} className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center mb-2">
            <span className="text-red-500 mr-2">⚠️</span>
            <span className="font-bold text-red-800">{extractText(flag.category)}</span>
            {flag.severity && (
              <span className="ml-2 text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                {extractText(flag.severity)}
              </span>
            )}
          </div>
          <p className="text-red-700 text-sm">{extractText(flag.description)}</p>
          {flag.clientTranslation && (
            <p className="text-gray-700 text-sm mt-2 italic">{extractText(flag.clientTranslation)}</p>
          )}
          {flag.actionNeeded && (
            <p className="text-red-600 text-sm mt-2 font-medium">Action: {extractText(flag.actionNeeded)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// Positive Signals Section
const PositiveSignalsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const signals = data?.positiveSignals;
  if (!signals || !Array.isArray(signals)) return null;

  return (
    <div className="positive-signals space-y-3">
      {signals.map((signal: any, idx: number) => (
        <div key={idx} className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center mb-2">
            <span className="text-green-500 mr-2">✓</span>
            <span className="font-bold text-green-800">{extractText(signal.category)}</span>
          </div>
          <p className="text-green-700 text-sm">{extractText(signal.description)}</p>
          {signal.clientBenefit && (
            <p className="text-gray-700 text-sm mt-2">Benefit: {extractText(signal.clientBenefit)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// Action Items Section
const ActionItemsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const items = data?.actionItems;
  if (!items || !Array.isArray(items)) return null;

  const getPriorityColor = (priority: string) => {
    const lower = priority?.toLowerCase() || '';
    if (lower.includes('high') || lower.includes('critical') || lower.includes('before')) return 'bg-red-50 border-red-200';
    if (lower.includes('medium') || lower.includes('shortly')) return 'bg-amber-50 border-amber-200';
    return 'bg-blue-50 border-blue-200';
  };

  return (
    <div className="action-items space-y-3">
      {items.map((item: any, idx: number) => (
        <div key={idx} className={`p-4 rounded-lg border ${getPriorityColor(item.priority || item.category)}`}>
          <div className="flex justify-between items-start mb-2">
            <span className="font-bold text-gray-800">{extractText(item.action)}</span>
            {item.priority && (
              <span className="text-xs px-2 py-1 bg-white rounded">{extractText(item.priority)}</span>
            )}
          </div>
          {item.details && <p className="text-gray-700 text-sm">{extractText(item.details)}</p>}
          <div className="flex gap-4 text-xs text-gray-500 mt-2">
            {item.deadline && <span>Deadline: {extractText(item.deadline)}</span>}
            {item.responsibleParty && <span>Responsible: {extractText(item.responsibleParty)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Conversation Points Section
const ConversationPointsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const points = data?.conversationPoints;
  if (!points || !Array.isArray(points)) return null;

  return (
    <div className="conversation-points space-y-4">
      {points.map((point: any, idx: number) => (
        <div key={idx} className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h4 className="font-bold text-purple-800 mb-2">{extractText(point.topic)}</h4>
          {point.context && (
            <p className="text-gray-700 text-sm mb-2">{extractText(point.context)}</p>
          )}
          {point.considerations && (
            <div className="bg-white p-3 rounded mt-2">
              <div className="text-sm font-medium text-gray-700 mb-1">Considerations:</div>
              <p className="text-gray-600 text-sm">{extractText(point.considerations)}</p>
            </div>
          )}
          {point.questionsToAsk && (
            <div className="mt-2 text-sm">
              <span className="font-medium text-purple-700">Questions to ask: </span>
              <span className="text-gray-700">{extractText(point.questionsToAsk)}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Glossary Section
const GlossarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const glossary = data?.glossary;
  if (!glossary || !Array.isArray(glossary)) return null;

  return (
    <div className="glossary">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {glossary.map((item: any, idx: number) => (
          <div key={idx} className="bg-gray-50 p-3 rounded-lg border">
            <div className="font-bold text-gray-800">{extractText(item.term)}</div>
            <p className="text-gray-700 text-sm mt-1">{extractText(item.definition)}</p>
            {item.whyItMatters && (
              <p className="text-blue-600 text-xs mt-2 italic">Why it matters: {extractText(item.whyItMatters)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// === Coverage Analysis Section Components ===

// Policy Overview Section
const PolicyOverviewSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const overview = data?.policyOverview;
  if (!overview) return null;

  return (
    <div className="policy-overview">
      <div className="grid grid-cols-2 gap-4 mb-4">
        {overview.policyType && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 font-medium uppercase">Policy Type</div>
            <div className="font-bold text-gray-800">{overview.policyType}</div>
          </div>
        )}
        {overview.policyPeriod && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 font-medium uppercase">Policy Period</div>
            <div className="font-bold text-gray-800">{overview.policyPeriod}</div>
          </div>
        )}
        {overview.namedInsured && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 col-span-2">
            <div className="text-xs text-blue-600 font-medium uppercase">Named Insured</div>
            <div className="font-bold text-gray-800">{overview.namedInsured}</div>
          </div>
        )}
      </div>
      {overview.coverageLines && Array.isArray(overview.coverageLines) && overview.coverageLines.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Coverage Lines</div>
          <div className="flex flex-wrap gap-2">
            {overview.coverageLines.map((line: string, idx: number) => (
              <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">{line}</span>
            ))}
          </div>
        </div>
      )}
      {overview.primaryLimits && (
        <div className="bg-gray-50 p-3 rounded-lg border">
          <div className="text-sm font-semibold text-gray-700 mb-2">Primary Limits</div>
          <div className="grid grid-cols-2 gap-4">
            {overview.primaryLimits.occurrence && (
              <div>
                <div className="text-xs text-gray-500">Per Occurrence</div>
                <div className="font-bold text-gray-800">{overview.primaryLimits.occurrence}</div>
              </div>
            )}
            {overview.primaryLimits.aggregate && (
              <div>
                <div className="text-xs text-gray-500">Aggregate</div>
                <div className="font-bold text-gray-800">{overview.primaryLimits.aggregate}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Coverage Recommendations Section
const CoverageRecommendationsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const recommendations = data?.coverageRecommendations;
  if (!recommendations || !Array.isArray(recommendations)) return null;

  const getPriorityColor = (priority: string) => {
    const p = priority?.toLowerCase() || '';
    if (p.includes('critical') || p.includes('high')) return 'bg-red-50 border-red-300 text-red-800';
    if (p.includes('medium')) return 'bg-yellow-50 border-yellow-300 text-yellow-800';
    return 'bg-green-50 border-green-300 text-green-800';
  };

  return (
    <div className="coverage-recommendations space-y-4">
      {recommendations.map((rec: any, idx: number) => (
        <div key={idx} className={`p-4 rounded-lg border ${getPriorityColor(rec.priority)}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold">{rec.category || 'Recommendation'}</span>
            <span className="px-2 py-1 text-xs rounded-full font-medium uppercase bg-white/50">
              {rec.priority}
            </span>
          </div>
          <p className="text-gray-800 mb-2">{extractText(rec.recommendation)}</p>
          {rec.rationale && (
            <p className="text-gray-600 text-sm italic">Rationale: {extractText(rec.rationale)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// Coverage Gaps & Opportunities Section
const CoverageGapsOpportunitiesSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const gapsData = data?.coverageGapsAndOpportunities;
  if (!gapsData) return null;

  return (
    <div className="coverage-gaps-opportunities">
      {gapsData.summary && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
          <p className="text-gray-700">{extractText(gapsData.summary)}</p>
        </div>
      )}
      {gapsData.gaps && Array.isArray(gapsData.gaps) && gapsData.gaps.length > 0 && (
        <div className="space-y-4">
          {gapsData.gaps.map((gap: any, idx: number) => (
            <div key={idx} className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-red-800">{gap.gapType || 'Coverage Gap'}</span>
              </div>
              <p className="text-gray-800 mb-2">{extractText(gap.description)}</p>
              {gap.impact && (
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-700">Impact: </span>
                  <span className="text-gray-600">{extractText(gap.impact)}</span>
                </div>
              )}
              {gap.recommendation && (
                <div className="p-2 bg-white rounded border border-red-100">
                  <span className="text-sm font-medium text-green-700">Recommendation: </span>
                  <span className="text-gray-700">{extractText(gap.recommendation)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// === Document Comparison / Policy Comparison Section Types ===

// Non-Standard Provisions Section
const NonStandardProvisionsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const provisions = data?.nonStandardProvisions;
  if (!provisions || !provisions.provisions || provisions.provisions.length === 0) return null;

  return (
    <div className="non-standard-provisions">
      {provisions.summary && (
        <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-gray-700">{extractText(provisions.summary)}</p>
        </div>
      )}
      <div className="space-y-4">
        {provisions.provisions.map((provision: any, idx: number) => (
          <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-800">{provision.title || provision.name || `Provision ${idx + 1}`}</span>
              {provision.severity && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  provision.severity === 'High' ? 'bg-red-100 text-red-800' :
                  provision.severity === 'Medium' ? 'bg-amber-100 text-amber-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {provision.severity}
                </span>
              )}
            </div>
            {provision.description && (
              <p className="text-gray-700 mb-2">{extractText(provision.description)}</p>
            )}
            {provision.impact && (
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-600">Impact: </span>
                <span className="text-gray-700">{extractText(provision.impact)}</span>
              </div>
            )}
            {provision.recommendation && (
              <div className="p-2 bg-blue-50 rounded border border-blue-100">
                <span className="text-sm font-medium text-blue-700">Recommendation: </span>
                <span className="text-gray-700">{extractText(provision.recommendation)}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Clause Wording Comparison Section
const ClauseWordingComparisonSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const clauseData = data?.clauseWordingComparison;
  if (!clauseData) return null;

  return (
    <div className="clause-wording-comparison">
      {clauseData.description && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
          <p className="text-gray-700">{extractText(clauseData.description)}</p>
        </div>
      )}
      {clauseData.analysis && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-2">Analysis</h4>
          <p className="text-gray-700">{extractText(clauseData.analysis)}</p>
        </div>
      )}
      {clauseData.comparisons && Array.isArray(clauseData.comparisons) && clauseData.comparisons.length > 0 && (
        <div className="space-y-4">
          {clauseData.comparisons.map((comparison: any, idx: number) => (
            <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="font-bold text-gray-800 mb-2">{comparison.clauseType || comparison.title || `Comparison ${idx + 1}`}</div>
              {comparison.policyA && (
                <div className="mb-2 p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-600">Policy A: </span>
                  <span className="text-gray-700">{extractText(comparison.policyA)}</span>
                </div>
              )}
              {comparison.policyB && (
                <div className="mb-2 p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-600">Policy B: </span>
                  <span className="text-gray-700">{extractText(comparison.policyB)}</span>
                </div>
              )}
              {comparison.difference && (
                <div className="p-2 bg-amber-50 rounded border border-amber-100">
                  <span className="text-sm font-medium text-amber-700">Key Difference: </span>
                  <span className="text-gray-700">{extractText(comparison.difference)}</span>
                </div>
              )}
              {comparison.impact && (
                <div className="mt-2 p-2 bg-red-50 rounded border border-red-100">
                  <span className="text-sm font-medium text-red-700">Impact: </span>
                  <span className="text-gray-700">{extractText(comparison.impact)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Coverage Analysis Matrices Section
const CoverageAnalysisMatricesSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const matrices = data?.coverageAnalysisMatrices;
  if (!matrices || !Array.isArray(matrices) || matrices.length === 0) return null;

  return (
    <div className="coverage-analysis-matrices space-y-6">
      {matrices.map((matrix: any, idx: number) => (
        <div key={idx} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {matrix.title && (
            <div className="bg-indigo-50 p-4 border-b border-indigo-200">
              <h3 className="text-lg font-bold text-indigo-800">{matrix.title}</h3>
              {matrix.description && (
                <p className="text-sm text-indigo-600 mt-1">{extractText(matrix.description)}</p>
              )}
            </div>
          )}
          {matrix.headers && matrix.rows && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    {matrix.headers.map((header: string, hIdx: number) => (
                      <th key={hIdx} className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row: any, rIdx: number) => (
                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {matrix.headers.map((header: string, cIdx: number) => {
                        const cellValue = row[header] || row[header.toLowerCase()] || Object.values(row)[cIdx] || '-';
                        return (
                          <td key={cIdx} className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                            {typeof cellValue === 'object' ? extractText(cellValue) : String(cellValue)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {matrix.insights && Array.isArray(matrix.insights) && matrix.insights.length > 0 && (
            <div className="p-4 bg-yellow-50 border-t border-yellow-200">
              <h4 className="font-medium text-yellow-800 mb-2">Key Insights</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                {matrix.insights.map((insight: string, iIdx: number) => (
                  <li key={iIdx}>{insight}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Risk Shift Navigator Section
const RiskShiftNavigatorSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const riskShift = data?.riskShift;
  if (!riskShift) return null;

  return (
    <div className="risk-shift-navigator">
      {/* TL;DR Summary - supports both string and {text, citations} formats */}
      {riskShift.tldr && (
        <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-gray-700">{extractText(typeof riskShift.tldr === 'string' ? riskShift.tldr : riskShift.tldr?.text || riskShift.tldr)}</p>
        </div>
      )}

      {/* Key Talking Points - array of strings */}
      {riskShift.keyTalkingPoints && Array.isArray(riskShift.keyTalkingPoints) && riskShift.keyTalkingPoints.length > 0 && (
        <div className="mb-4">
          <h4 className="font-semibold text-gray-800 mb-2">Key Talking Points</h4>
          <ul className="list-disc list-inside space-y-2">
            {riskShift.keyTalkingPoints.map((point: string, idx: number) => (
              <li key={idx} className="text-gray-700">{point}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Detailed Comparison Table - {headers, rows} format */}
      {riskShift.detailedComparisonTable?.headers && riskShift.detailedComparisonTable?.rows && (
        <div className="mb-4">
          <h4 className="font-semibold text-gray-800 mb-2">Detailed Comparison</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  {riskShift.detailedComparisonTable.headers.map((header: string, idx: number) => (
                    <th key={idx} className="border border-gray-300 px-4 py-2 text-left">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {riskShift.detailedComparisonTable.rows.map((row: string[], rowIdx: number) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell: string, cellIdx: number) => (
                      <td key={cellIdx} className="border border-gray-300 px-4 py-2">{extractText(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Meta Analysis - complex object with multiple sub-sections */}
      {riskShift.metaAnalysis && (
        <div className="space-y-4">
          {/* Suspicious Absences */}
          {riskShift.metaAnalysis.suspiciousAbsences && riskShift.metaAnalysis.suspiciousAbsences.length > 0 && (
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">Suspicious Absences</h4>
              <ul className="list-disc list-inside space-y-1">
                {riskShift.metaAnalysis.suspiciousAbsences.map((item: string, idx: number) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Interconnectivity Analysis */}
          {riskShift.metaAnalysis.interconnectivityAnalysis && riskShift.metaAnalysis.interconnectivityAnalysis.length > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">Interconnectivity Analysis</h4>
              <div className="space-y-3">
                {riskShift.metaAnalysis.interconnectivityAnalysis.map((item: any, idx: number) => (
                  <div key={idx} className="border-l-2 border-blue-300 pl-3">
                    <div className="font-medium text-gray-800">{item.interaction}</div>
                    {item.explanation && (
                      <p className="text-sm text-gray-600 mt-1">{item.explanation}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strategic Actionability */}
          {riskShift.metaAnalysis.strategicActionability && riskShift.metaAnalysis.strategicActionability.length > 0 && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-indigo-800 mb-2">Strategic Actions</h4>
              <div className="space-y-3">
                {riskShift.metaAnalysis.strategicActionability.map((action: any, idx: number) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${
                      action.priority === 'High' ? 'bg-red-100 text-red-800' :
                      action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>{action.priority}</span>
                    <div>
                      <span className="font-medium text-gray-800">{action.action}</span>
                      {action.rationale && (
                        <p className="text-sm text-gray-600 mt-1">{action.rationale}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blind Spot Acknowledgment */}
          {riskShift.metaAnalysis.blindSpotAcknowledgment && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800 mb-2">Blind Spot Acknowledgment</h4>
              {riskShift.metaAnalysis.blindSpotAcknowledgment.text && (
                <p className="text-gray-700 mb-2">{riskShift.metaAnalysis.blindSpotAcknowledgment.text}</p>
              )}
              {riskShift.metaAnalysis.blindSpotAcknowledgment.missingDocuments &&
               riskShift.metaAnalysis.blindSpotAcknowledgment.missingDocuments.length > 0 && (
                <div className="mt-2">
                  <span className="text-sm font-medium text-gray-600">Missing Documents:</span>
                  <ul className="list-disc list-inside mt-1">
                    {riskShift.metaAnalysis.blindSpotAcknowledgment.missingDocuments.map((doc: string, idx: number) => (
                      <li key={idx} className="text-sm text-gray-600">{doc}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Professional Disclaimer */}
          {riskShift.metaAnalysis.professionalDisclaimer && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-700 mb-2">Professional Disclaimer</h4>
              <p className="text-sm text-gray-600 italic">{riskShift.metaAnalysis.professionalDisclaimer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Scenario Testing Section
const ScenarioTestingSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const scenarioData = data?.scenarioTesting;
  if (!scenarioData) return null;

  return (
    <div className="scenario-testing">
      {scenarioData.summary && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
          <p className="text-gray-700">{extractText(scenarioData.summary)}</p>
        </div>
      )}
      {scenarioData.scenarios && Array.isArray(scenarioData.scenarios) && scenarioData.scenarios.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Scenario</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Coverage</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {scenarioData.scenarios.map((scenario: any, idx: number) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-4 py-2 font-medium">{extractText(scenario.scenario)}</td>
                  <td className="border border-gray-300 px-4 py-2">{extractText(scenario.coverage)}</td>
                  <td className="border border-gray-300 px-4 py-2">{extractText(scenario.outcome)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Coverage Metrics Section
const CoverageMetricsSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const metrics = data?.coverageMetrics;
  if (!metrics) return null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="coverage-metrics">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
          <div className="text-xs text-blue-600 font-medium uppercase mb-1">Overall Rating</div>
          <div className="font-bold text-xl text-blue-800">{metrics.overallCoverageRating || 'N/A'}</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
          <div className="text-xs text-gray-600 font-medium uppercase mb-1">Completeness Score</div>
          <div className={`font-bold text-xl ${getScoreColor(metrics.coverageCompletenessScore || 0)}`}>
            {metrics.coverageCompletenessScore !== undefined ? `${metrics.coverageCompletenessScore}%` : 'N/A'}
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
          <div className="text-xs text-green-600 font-medium uppercase mb-1">Recommended Action</div>
          <div className="font-bold text-sm text-green-800">{metrics.recommendedAction || 'N/A'}</div>
        </div>
      </div>
    </div>
  );
};

// Meta Analysis Section
const MetaAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const meta = data?.metaAnalysis;
  if (!meta) return null;

  return (
    <div className="meta-analysis space-y-4">
      {meta.general && (
        <div className="p-4 bg-gray-50 rounded-lg border">
          <p className="text-gray-700">{extractText(meta.general)}</p>
        </div>
      )}
      {meta.antiPatterns && (
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h4 className="font-bold text-yellow-800 mb-2">{meta.antiPatterns.title || 'Anti-Patterns'}</h4>
          <p className="text-gray-700">{extractText(meta.antiPatterns.analysis)}</p>
        </div>
      )}
      {meta.blindSpotAcknowledgment && (
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <h4 className="font-bold text-orange-800 mb-2">{meta.blindSpotAcknowledgment.title || 'Blind Spots'}</h4>
          <p className="text-gray-700">{extractText(meta.blindSpotAcknowledgment.acknowledgment)}</p>
        </div>
      )}
      {meta.aiConfidenceMandate && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-bold text-blue-800 mb-2">AI Confidence</h4>
          <p className="text-gray-700">{extractText(meta.aiConfidenceMandate)}</p>
        </div>
      )}
      {meta.professionalDisclaimer && (
        <div className="p-4 bg-gray-100 rounded-lg border">
          <h4 className="font-bold text-gray-700 mb-2">Professional Disclaimer</h4>
          <p className="text-gray-600 text-sm">{extractText(meta.professionalDisclaimer)}</p>
        </div>
      )}
    </div>
  );
};

// === Portfolio Analyzer Section Components ===

// Portfolio Analyzer Executive Summary Section
const PortfolioExecutiveSummarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const summary = data?.executiveSummary;
  if (!summary) return null;

  return (
    <div className="portfolio-executive-summary space-y-6">
      {/* TL;DR */}
      {summary.tldr && (
        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
          <h3 className="font-semibold text-blue-800 mb-2">TL;DR</h3>
          <p className="text-gray-700">{extractText(summary.tldr)}</p>
        </div>
      )}

      {/* Bottom Line */}
      {summary.bottomLine && (
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
          <h3 className="font-semibold text-emerald-800 mb-3">Bottom Line</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {summary.bottomLine.maximumAvailableCoverage !== undefined && (
              <div><span className="text-gray-600">Max Coverage:</span> <strong>${summary.bottomLine.maximumAvailableCoverage?.toLocaleString()}</strong></div>
            )}
            {summary.bottomLine.probableRecovery !== undefined && (
              <div><span className="text-gray-600">Probable Recovery:</span> <strong>${summary.bottomLine.probableRecovery?.toLocaleString()}</strong></div>
            )}
            {summary.bottomLine.insuredTotalRetention !== undefined && (
              <div><span className="text-gray-600">Insured Retention:</span> <strong>${summary.bottomLine.insuredTotalRetention?.toLocaleString()}</strong></div>
            )}
            {summary.bottomLine.coverageGaps !== undefined && (
              <div><span className="text-gray-600">Coverage Gaps:</span> <strong>{summary.bottomLine.coverageGaps}</strong></div>
            )}
          </div>
        </div>
      )}

      {/* Key Findings */}
      {summary.keyFindings && summary.keyFindings.length > 0 && (
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
          <h3 className="font-semibold text-amber-800 mb-3">Key Findings</h3>
          <ul className="space-y-2">
            {summary.keyFindings.map((finding: any, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <span className="text-gray-700">{extractText(finding)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Critical Issues */}
      {summary.criticalIssues && summary.criticalIssues.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h3 className="font-semibold text-red-800 mb-3">Critical Issues</h3>
          <ul className="space-y-2">
            {summary.criticalIssues.map((issue: any, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-red-500 mt-0.5">⚠</span>
                <span className="text-gray-700">{extractText(issue)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Portfolio Structure Section
const PortfolioStructureSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const structure = data?.portfolioStructure;
  if (!structure) return null;

  return (
    <div className="portfolio-structure space-y-6">
      {/* Vertical Towers */}
      {structure.verticalTowers && structure.verticalTowers.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 mb-3">Vertical Towers</h3>
          <div className="space-y-4">
            {structure.verticalTowers.map((tower: any, idx: number) => (
              <div key={idx} className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-blue-800">{tower.coverageLine}</h4>
                  {tower.totalVerticalLimit !== undefined && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                      Total Limit: ${tower.totalVerticalLimit?.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Layers */}
                {tower.layers && tower.layers.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 pr-4">Layer</th>
                          <th className="text-left py-2 pr-4">Carrier</th>
                          <th className="text-right py-2 pr-4">Limit</th>
                          <th className="text-right py-2 pr-4">Attachment</th>
                          <th className="text-left py-2">Defense Costs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tower.layers.map((layer: any, layerIdx: number) => (
                          <tr key={layerIdx} className="border-b border-blue-100">
                            <td className="py-2 pr-4">{layer.layerType || `Layer ${layer.layerNumber || layerIdx + 1}`}</td>
                            <td className="py-2 pr-4">{layer.carrier}</td>
                            <td className="py-2 pr-4 text-right">${layer.limit?.toLocaleString()}</td>
                            <td className="py-2 pr-4 text-right">${layer.attachmentPoint?.toLocaleString()}</td>
                            <td className="py-2">{layer.defenseCostsPosition || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Gaps */}
                {tower.gapsIdentified && tower.gapsIdentified.length > 0 && (
                  <div className="mt-3 p-2 bg-red-50 rounded border border-red-200">
                    <span className="text-red-700 font-medium text-sm">Gaps: </span>
                    <span className="text-red-600 text-sm">{tower.gapsIdentified.join(', ')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Horizontal Coverage Lines */}
      {structure.horizontalCoverageLines && structure.horizontalCoverageLines.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 mb-3">Horizontal Coverage Lines</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm bg-white rounded-lg overflow-hidden border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left py-3 px-4">Coverage Line</th>
                  <th className="text-left py-3 px-4">Carriers</th>
                  <th className="text-right py-3 px-4">Primary Limit</th>
                  <th className="text-right py-3 px-4">Total Available</th>
                  <th className="text-left py-3 px-4">Position</th>
                </tr>
              </thead>
              <tbody>
                {structure.horizontalCoverageLines.map((line: any, idx: number) => (
                  <tr key={idx} className="border-t">
                    <td className="py-3 px-4 font-medium">{line.coverageLine}</td>
                    <td className="py-3 px-4">{line.carriers?.join(', ')}</td>
                    <td className="py-3 px-4 text-right">${line.primaryLimit?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">${line.totalAvailable?.toLocaleString()}</td>
                    <td className="py-3 px-4">{line.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Scenario Analysis Section
const ScenarioAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const scenario = data?.scenarioAnalysis;
  if (!scenario) return null;

  return (
    <div className="scenario-analysis space-y-6">
      {/* Scenario Name & Description */}
      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
        <h3 className="font-semibold text-purple-800 text-lg mb-2">{scenario.scenarioName}</h3>
        {scenario.scenarioDescription && (
          <p className="text-gray-700">{extractText(scenario.scenarioDescription)}</p>
        )}
      </div>

      {/* Components */}
      {scenario.components && scenario.components.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-800 mb-3">Scenario Components</h3>
          <div className="space-y-3">
            {scenario.components.map((component: any, idx: number) => (
              <div key={idx} className="bg-white p-4 rounded-lg border shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-800">{component.componentName}</h4>
                  {component.estimatedExposure !== undefined && (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
                      Exposure: ${component.estimatedExposure?.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {component.exposureType && (
                    <div><span className="text-gray-600">Exposure Type:</span> <span className="font-medium">{component.exposureType}</span></div>
                  )}
                  {component.trigger && (
                    <div><span className="text-gray-600">Trigger:</span> <span className="font-medium">{component.trigger}</span></div>
                  )}
                </div>
                {component.respondingLines && component.respondingLines.length > 0 && (
                  <div className="mt-2">
                    <span className="text-gray-600 text-sm">Responding Lines: </span>
                    <span className="text-sm">{component.respondingLines.join(', ')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Portfolio Coverage Analysis Section (array of components with layer analysis)
const PortfolioCoverageAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const coverageAnalysis = data?.coverageAnalysis;
  if (!coverageAnalysis || !Array.isArray(coverageAnalysis)) return null;

  return (
    <div className="portfolio-coverage-analysis space-y-6">
      {coverageAnalysis.map((component: any, idx: number) => (
        <div key={idx} className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gray-100 px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-800">{component.componentName}</h3>
          </div>
          <div className="p-4">
            {/* Layer Analysis Table */}
            {component.layerAnalysis && component.layerAnalysis.length > 0 && (
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Layer</th>
                      <th className="text-left py-2 pr-4">Type</th>
                      <th className="text-center py-2 pr-4">Responds</th>
                      <th className="text-right py-2 pr-4">Available</th>
                      <th className="text-right py-2 pr-4">Probability</th>
                    </tr>
                  </thead>
                  <tbody>
                    {component.layerAnalysis.map((layer: any, layerIdx: number) => (
                      <tr key={layerIdx} className="border-b">
                        <td className="py-2 pr-4">{layer.layerName}</td>
                        <td className="py-2 pr-4">{layer.layerType}</td>
                        <td className="py-2 pr-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            layer.responds?.toLowerCase() === 'yes' || layer.responds?.toLowerCase() === 'likely'
                              ? 'bg-green-100 text-green-800'
                              : layer.responds?.toLowerCase() === 'no' || layer.responds?.toLowerCase() === 'unlikely'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {layer.responds}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right">${layer.availableCoverage?.toLocaleString()}</td>
                        <td className="py-2 pr-4 text-right">{layer.probability}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Total Vertical Coverage */}
            {component.totalVerticalCoverage && (
              <div className="bg-blue-50 p-3 rounded border border-blue-200 flex gap-6">
                {component.totalVerticalCoverage.maximumAvailable !== undefined && (
                  <div><span className="text-blue-700 font-medium">Max Available:</span> <strong>${component.totalVerticalCoverage.maximumAvailable?.toLocaleString()}</strong></div>
                )}
                {component.totalVerticalCoverage.probableRecovery !== undefined && (
                  <div><span className="text-blue-700 font-medium">Probable Recovery:</span> <strong>${component.totalVerticalCoverage.probableRecovery?.toLocaleString()}</strong></div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Portfolio Synthesis Section
const PortfolioSynthesisSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const synthesis = data?.portfolioSynthesis;
  if (!synthesis) return null;

  return (
    <div className="portfolio-synthesis space-y-6">
      {/* Total Portfolio Exposure */}
      {synthesis.totalPortfolioExposure && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-lg text-white">
          <h3 className="font-semibold text-white mb-4">Total Portfolio Exposure</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {synthesis.totalPortfolioExposure.maximumAvailableCoverage !== undefined && (
              <div>
                <div className="text-blue-100 text-sm">Max Available Coverage</div>
                <div className="text-2xl font-bold">${synthesis.totalPortfolioExposure.maximumAvailableCoverage?.toLocaleString()}</div>
              </div>
            )}
            {synthesis.totalPortfolioExposure.probableRecovery !== undefined && (
              <div>
                <div className="text-blue-100 text-sm">Probable Recovery</div>
                <div className="text-2xl font-bold">${synthesis.totalPortfolioExposure.probableRecovery?.toLocaleString()}</div>
              </div>
            )}
            {synthesis.totalPortfolioExposure.totalInsuredPays !== undefined && (
              <div>
                <div className="text-blue-100 text-sm">Insured Pays</div>
                <div className="text-2xl font-bold">${synthesis.totalPortfolioExposure.totalInsuredPays?.toLocaleString()}</div>
              </div>
            )}
            {synthesis.totalPortfolioExposure.totalUninsuredExposure !== undefined && (
              <div>
                <div className="text-blue-100 text-sm">Uninsured Exposure</div>
                <div className="text-2xl font-bold">${synthesis.totalPortfolioExposure.totalUninsuredExposure?.toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coverage Gaps */}
      {synthesis.coverageGaps && synthesis.coverageGaps.length > 0 && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h3 className="font-semibold text-red-800 mb-3">Coverage Gaps</h3>
          <div className="space-y-3">
            {synthesis.coverageGaps.map((gap: any, idx: number) => (
              <div key={idx} className="bg-white p-3 rounded border border-red-100">
                <div className="flex justify-between items-start">
                  <span className="text-gray-800">{gap.description}</span>
                  {gap.estimatedExposure !== undefined && (
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-sm font-medium">
                      ${gap.estimatedExposure?.toLocaleString()}
                    </span>
                  )}
                </div>
                {gap.location && (
                  <div className="text-gray-500 text-sm mt-1">Location: {gap.location}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Portfolio Assessment Section
const PortfolioAssessmentSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const assessment = data?.assessment;
  if (!assessment) return null;

  const getLevelColor = (level: string) => {
    const lowerLevel = level?.toLowerCase() || '';
    if (lowerLevel.includes('high') || lowerLevel.includes('strong')) return 'text-green-700 bg-green-100';
    if (lowerLevel.includes('low') || lowerLevel.includes('weak')) return 'text-red-700 bg-red-100';
    return 'text-yellow-700 bg-yellow-100';
  };

  return (
    <div className="portfolio-assessment space-y-6">
      {/* Coverage Certainty */}
      {assessment.portfolioCoverageCertainty && (
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Coverage Certainty</h3>
            <span className={`px-3 py-1 rounded-full font-medium ${getLevelColor(assessment.portfolioCoverageCertainty.level)}`}>
              {assessment.portfolioCoverageCertainty.level}
            </span>
          </div>
          {assessment.portfolioCoverageCertainty.reasoning && (
            <p className="text-gray-700">{extractText(assessment.portfolioCoverageCertainty.reasoning)}</p>
          )}
        </div>
      )}

      {/* Denial Risk */}
      {assessment.portfolioDenialRisk && (
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Denial Risk</h3>
            <span className={`px-3 py-1 rounded-full font-medium ${
              assessment.portfolioDenialRisk.level?.toLowerCase().includes('high') ? 'text-red-700 bg-red-100' :
              assessment.portfolioDenialRisk.level?.toLowerCase().includes('low') ? 'text-green-700 bg-green-100' :
              'text-yellow-700 bg-yellow-100'
            }`}>
              {assessment.portfolioDenialRisk.level}
            </span>
          </div>
          {assessment.portfolioDenialRisk.reasoning && (
            <p className="text-gray-700">{extractText(assessment.portfolioDenialRisk.reasoning)}</p>
          )}
        </div>
      )}

      {/* Analysis Confidence */}
      {assessment.analysisConfidence && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Analysis Confidence</h3>
            {assessment.analysisConfidence.percentage !== undefined && (
              <span className="text-2xl font-bold text-blue-600">{assessment.analysisConfidence.percentage}%</span>
            )}
          </div>
          {assessment.analysisConfidence.reasoning && (
            <p className="text-gray-700 mb-3">{assessment.analysisConfidence.reasoning}</p>
          )}
          {assessment.analysisConfidence.factorsReducingConfidence && assessment.analysisConfidence.factorsReducingConfidence.length > 0 && (
            <div className="mt-2">
              <span className="text-gray-600 font-medium text-sm">Factors reducing confidence:</span>
              <ul className="mt-1 space-y-1">
                {assessment.analysisConfidence.factorsReducingConfidence.map((factor: string, idx: number) => (
                  <li key={idx} className="text-gray-600 text-sm flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    {factor}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Blind Spots Section (Portfolio Analyzer)
const BlindSpotsSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const blindSpots = data?.blindSpots;
  if (!blindSpots) return null;

  return (
    <div className="blind-spots space-y-4">
      {blindSpots.missingPolicies && blindSpots.missingPolicies.length > 0 && (
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <h3 className="font-semibold text-orange-800 mb-2">Missing Policies</h3>
          <ul className="space-y-1">
            {blindSpots.missingPolicies.map((policy: string, idx: number) => (
              <li key={idx} className="text-gray-700 flex items-start gap-2">
                <span className="text-orange-500">•</span>
                {policy}
              </li>
            ))}
          </ul>
        </div>
      )}

      {blindSpots.unknownClaimsHistory && (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h3 className="font-semibold text-yellow-800 mb-2">Unknown Claims History</h3>
          <p className="text-gray-700">{blindSpots.unknownClaimsHistory}</p>
        </div>
      )}

      {blindSpots.carrierCoordinationUncertainty && (
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="font-semibold text-purple-800 mb-2">Carrier Coordination Uncertainty</h3>
          <p className="text-gray-700">{blindSpots.carrierCoordinationUncertainty}</p>
        </div>
      )}

      {blindSpots.otherLimitations && blindSpots.otherLimitations.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="font-semibold text-gray-800 mb-2">Other Limitations</h3>
          <ul className="space-y-1">
            {blindSpots.otherLimitations.map((limitation: string, idx: number) => (
              <li key={idx} className="text-gray-700 flex items-start gap-2">
                <span className="text-gray-500">•</span>
                {limitation}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// === Rate Analysis Section Components ===

// Rate Analysis Executive Summary Section
const RateAnalysisExecutiveSummarySection: React.FC<{ data: any } & SectionEditProps> = ({
  data,
  isEditMode,
  editedContent,
  onContentChange
}) => {
  const summary = data?.executiveSummary;
  if (!summary) return null;

  const getDirectionColor = (direction: string) => {
    if (direction === 'increase') return 'text-red-600 bg-red-100';
    if (direction === 'decrease') return 'text-green-600 bg-green-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getDirectionIcon = (direction: string) => {
    if (direction === 'increase') return '↑';
    if (direction === 'decrease') return '↓';
    return '→';
  };

  return (
    <div className="rate-analysis-executive-summary space-y-6">
      {/* TL;DR */}
      {summary.tldr && (
        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
          <h3 className="font-semibold text-blue-800 mb-2">TL;DR</h3>
          <p className="text-gray-700">{extractText(summary.tldr)}</p>
        </div>
      )}

      {/* Analysis Tier & Policy Identification */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {summary.analysisTier && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <div className="text-sm font-medium text-gray-600">Analysis Tier</div>
            <div className="text-lg font-semibold text-gray-800">{summary.analysisTier}</div>
          </div>
        )}
        {summary.policyIdentification && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <div className="text-sm font-medium text-gray-600">Policy Identification</div>
            <div className="text-gray-800">{extractText(summary.policyIdentification)}</div>
          </div>
        )}
      </div>

      {/* Periods Analyzed */}
      {summary.periodsAnalyzed && Array.isArray(summary.periodsAnalyzed) && summary.periodsAnalyzed.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <div className="text-sm font-medium text-gray-600 mb-2">Periods Analyzed</div>
          <div className="flex flex-wrap gap-2">
            {summary.periodsAnalyzed.map((period: string, idx: number) => (
              <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-sm">{period}</span>
            ))}
          </div>
        </div>
      )}

      {/* Total Premium Change */}
      {summary.totalPremiumChange && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-lg text-white">
          <h3 className="font-semibold text-white mb-3">Total Premium Change</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-indigo-100 text-sm">Amount</div>
              <div className="text-2xl font-bold">
                ${summary.totalPremiumChange.amount?.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-indigo-100 text-sm">Percentage</div>
              <div className="text-2xl font-bold">
                {summary.totalPremiumChange.percentage > 0 ? '+' : ''}{summary.totalPremiumChange.percentage?.toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-indigo-100 text-sm">Direction</div>
              <div className={`text-xl font-bold px-3 py-1 rounded inline-block ${getDirectionColor(summary.totalPremiumChange.direction)}`}>
                {getDirectionIcon(summary.totalPremiumChange.direction)} {summary.totalPremiumChange.direction}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Driver */}
      {summary.primaryDriver && (
        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
          <h3 className="font-semibold text-indigo-800 mb-2">Primary Driver</h3>
          <p className="text-gray-700">{extractText(summary.primaryDriver)}</p>
        </div>
      )}

      {/* Secondary Drivers */}
      {summary.secondaryDrivers && Array.isArray(summary.secondaryDrivers) && summary.secondaryDrivers.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="font-semibold text-gray-800 mb-3">Secondary Drivers</h3>
          <ul className="space-y-2">
            {summary.secondaryDrivers.map((driver: any, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5">•</span>
                <span className="text-gray-700">{extractText(driver)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Executive Narrative */}
      {summary.executiveNarrative && (
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-2">Executive Narrative</h3>
          <p className="text-gray-700">{extractText(summary.executiveNarrative)}</p>
        </div>
      )}
    </div>
  );
};

// Rate Analysis Tables Section (analysisTables array)
const RateAnalysisTablesSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const tables = data?.analysisTables;
  if (!tables || !Array.isArray(tables)) return null;

  // Helper to safely extract value from cell (handles RichTextWithCitations)
  const getCellValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object' && value.text !== undefined) return extractText(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className="rate-analysis-tables space-y-8">
      {tables.map((table: any, tableIdx: number) => (
        <div key={tableIdx} className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <div className="bg-indigo-50 px-4 py-3 border-b">
            <h3 className="font-semibold text-indigo-800">{table.title}</h3>
            {table.description && (
              <p className="text-sm text-indigo-600 mt-1">{typeof table.description === 'object' ? extractText(table.description) : table.description}</p>
            )}
          </div>
          <div className="p-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  {table.headers?.map((header: string, hIdx: number) => (
                    <th key={hIdx} className="text-left py-2 px-3 font-semibold text-gray-700">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows?.map((row: any, rIdx: number) => (
                  <tr key={rIdx} className="border-b border-gray-100">
                    {table.headers?.map((header: string, cIdx: number) => {
                      const cellValue = row[header] ?? row[header.toLowerCase()] ?? row[Object.keys(row)[cIdx]];
                      return (
                        <td key={cIdx} className="py-2 px-3 text-gray-600">
                          {getCellValue(cellValue)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              {table.totals && (
                <tfoot className="bg-gray-50 font-semibold">
                  <tr>
                    {table.headers?.map((header: string, tIdx: number) => (
                      <td key={tIdx} className="py-2 px-3">{getCellValue(table.totals[header])}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {table.insights && table.insights.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t">
              <h4 className="font-medium text-gray-700 mb-2">Insights</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                {table.insights.map((insight: any, iIdx: number) => (
                  <li key={iIdx} className="flex items-start gap-2">
                    <span className="text-indigo-500">•</span>
                    {typeof insight === 'object' ? extractText(insight) : String(insight)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Rate Analysis Driver Hierarchy Section
const RateDriverHierarchySection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const hierarchy = data?.driverHierarchy;
  if (!hierarchy) return null;

  return (
    <div className="rate-driver-hierarchy space-y-4">
      {/* Primary Driver */}
      {hierarchy.primaryDriver && (
        <div className="bg-indigo-100 rounded-lg p-6 border-2 border-indigo-300">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-indigo-800">
              Primary Driver: {hierarchy.primaryDriver.component}
            </h3>
            {hierarchy.primaryDriver.percentageOfTotal !== undefined && (
              <span className="px-3 py-1 bg-indigo-200 text-indigo-800 rounded-full text-sm font-semibold">
                {hierarchy.primaryDriver.percentageOfTotal}% of total
              </span>
            )}
          </div>
          {hierarchy.primaryDriver.impact && (
            <p className="text-gray-700">{extractText(hierarchy.primaryDriver.impact)}</p>
          )}
        </div>
      )}

      {/* Secondary Drivers */}
      {hierarchy.secondaryDrivers && Array.isArray(hierarchy.secondaryDrivers) && hierarchy.secondaryDrivers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hierarchy.secondaryDrivers.map((driver: any, idx: number) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-700">{driver.component}</h4>
                {driver.percentageOfTotal !== undefined && (
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-sm">
                    {driver.percentageOfTotal}%
                  </span>
                )}
              </div>
              {driver.impact && (
                <p className="text-sm text-gray-600">{extractText(driver.impact)}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Interaction Effects */}
      {hierarchy.interactionEffects && (
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 mt-4">
          <h4 className="font-semibold text-purple-800 mb-2">Interaction Effects</h4>
          <p className="text-gray-700">{extractText(hierarchy.interactionEffects)}</p>
        </div>
      )}
    </div>
  );
};

// Rate Analysis Weak Signals Section
const RateWeakSignalsSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const signals = data?.weakSignals;
  if (!signals || !Array.isArray(signals)) return null;

  return (
    <div className="rate-weak-signals space-y-4">
      {signals.map((signal: any, idx: number) => (
        <div key={idx} className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-3 mb-2">
            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-medium">
              {signal.category}
            </span>
            <span className="font-semibold text-gray-800">{signal.indicator}</span>
          </div>
          {signal.whereDetected && (
            <div className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Detected in:</span> {signal.whereDetected}
            </div>
          )}
          {signal.pattern && (
            <div className="mb-2">
              <span className="font-medium text-gray-700">Pattern:</span>
              <p className="text-gray-600 text-sm">{extractText(signal.pattern)}</p>
            </div>
          )}
          {signal.significance && (
            <div className="bg-white p-2 rounded border border-amber-200">
              <span className="font-medium text-amber-700">Significance:</span>
              <p className="text-gray-700 text-sm">{extractText(signal.significance)}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Rate Analysis Trend Analysis Section
const RateTrendAnalysisSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const trend = data?.trendAnalysis;
  if (!trend) return null;

  const formatPercentage = (value: number | undefined) => {
    if (value === undefined) return 'N/A';
    return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : String(value);
  };

  return (
    <div className="rate-trend-analysis space-y-6">
      {/* CAGR Metrics */}
      {trend.cagr && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {trend.cagr.premium !== undefined && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="text-sm font-medium text-blue-600">Premium CAGR</div>
              <div className="text-2xl font-bold text-blue-800">{formatPercentage(trend.cagr.premium)}</div>
            </div>
          )}
          {trend.cagr.rate !== undefined && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="text-sm font-medium text-green-600">Rate CAGR</div>
              <div className="text-2xl font-bold text-green-800">{formatPercentage(trend.cagr.rate)}</div>
            </div>
          )}
          {trend.cagr.basis !== undefined && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="text-sm font-medium text-purple-600">Basis CAGR</div>
              <div className="text-2xl font-bold text-purple-800">{formatPercentage(trend.cagr.basis)}</div>
            </div>
          )}
        </div>
      )}

      {/* Trend Strength */}
      {trend.trendStrength && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="text-sm font-medium text-gray-600">Trend Strength</div>
          <div className="text-lg font-semibold text-gray-800">{trend.trendStrength}</div>
        </div>
      )}

      {/* Trajectory */}
      {trend.trajectory && (
        <div className="bg-white p-4 rounded-lg border">
          <h4 className="font-semibold text-gray-700 mb-2">Trajectory</h4>
          <p className="text-gray-600">{extractText(trend.trajectory)}</p>
        </div>
      )}

      {/* Inflection Points */}
      {trend.inflectionPoints && Array.isArray(trend.inflectionPoints) && trend.inflectionPoints.length > 0 && (
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <h4 className="font-medium text-yellow-800 mb-2">Inflection Points</h4>
          <ul className="list-disc list-inside space-y-1 text-yellow-700">
            {trend.inflectionPoints.map((point: string, idx: number) => (
              <li key={idx}>{point}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Rate Analysis Key Findings Section
const RateKeyFindingsSection: React.FC<{ data: any } & SectionEditProps> = ({ data }) => {
  const keyFindings = data?.keyFindings;
  if (!keyFindings) return null;

  const getMagnitudeColor = (magnitude: string) => {
    if (magnitude?.includes('Material')) return 'bg-red-100 text-red-800';
    if (magnitude?.includes('Moderate')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="rate-key-findings space-y-4">
      {keyFindings.summary && (
        <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
          <p className="text-gray-700">{keyFindings.summary}</p>
        </div>
      )}

      {keyFindings.findings && Array.isArray(keyFindings.findings) && keyFindings.findings.length > 0 && (
        <div className="space-y-4">
          {keyFindings.findings.map((finding: any, idx: number) => (
            <div key={idx} className="bg-white rounded-lg border shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                <span className="font-semibold text-gray-700">{finding.category}</span>
                <div className="flex gap-2">
                  {finding.magnitude && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getMagnitudeColor(finding.magnitude)}`}>
                      {finding.magnitude}
                    </span>
                  )}
                  {finding.confidence && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {finding.confidence}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4 space-y-2">
                {finding.finding?.observation && (
                  <div>
                    <span className="font-medium text-gray-700">Observation:</span>
                    <p className="text-gray-600">{extractText(finding.finding.observation)}</p>
                  </div>
                )}
                {finding.finding?.impact && (
                  <div>
                    <span className="font-medium text-gray-700">Impact:</span>
                    <p className="text-gray-600">{finding.finding.impact}</p>
                  </div>
                )}
                {finding.finding?.recommendation && (
                  <div className="bg-blue-50 p-2 rounded">
                    <span className="font-medium text-blue-700">Recommendation:</span>
                    <p className="text-blue-600 text-sm">{finding.finding.recommendation}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Umbrella/Homeowners Generic Section - renders structured data nicely
const UmbrellaGenericSection: React.FC<{ data: any; contentKey: string }> = ({ data, contentKey }) => {
  const content = data?.[contentKey];
  if (!content) return null;

  const stripCitations = (text: string): string => {
    if (typeof text !== 'string') return String(text || '');
    return text.replace(/\[CITE:\s*"[^"]*"\]/g, '').trim();
  };

  const renderValue = (value: any, depth: number = 0): React.ReactNode => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return stripCitations(value);

    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      // Array of strings
      if (typeof value[0] === 'string') {
        return (
          <ul className="list-disc ml-4 space-y-1">
            {value.map((item, i) => <li key={i} className="text-sm text-gray-700">{stripCitations(item)}</li>)}
          </ul>
        );
      }
      // Array of objects - render as cards
      return (
        <div className="space-y-3">
          {value.map((item, i) => (
            <div key={i} className="bg-gray-50 rounded p-3 border border-gray-200">
              {typeof item === 'object' && item !== null ? renderObject(item, depth + 1) : <span className="text-sm">{String(item)}</span>}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') return renderObject(value, depth);
    return String(value);
  };

  const formatKey = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, s => s.toUpperCase())
      .replace(/_/g, ' ')
      .trim();
  };

  const renderObject = (obj: any, depth: number = 0): React.ReactNode => {
    const entries = Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return null;

    return (
      <div className={`space-y-2 ${depth > 0 ? 'ml-2' : ''}`}>
        {entries.map(([key, value]) => {
          // Skip metadata keys
          if (key === 'citations' || key === 'responseMetadata') return null;
          const rendered = renderValue(value, depth);
          if (rendered === null) return null;

          // Simple value on one line
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return (
              <div key={key} className="flex flex-wrap gap-1">
                <span className="text-sm font-medium text-gray-600">{formatKey(key)}:</span>
                <span className="text-sm text-gray-800">{rendered}</span>
              </div>
            );
          }

          // Complex value with label above
          return (
            <div key={key} className="mb-3">
              <h4 className={`text-sm font-semibold text-gray-700 mb-1 ${depth === 0 ? 'text-base' : ''}`}>{formatKey(key)}</h4>
              {rendered}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="umbrella-section">
      {typeof content === 'string' ? (
        <p className="text-sm text-gray-700">{stripCitations(content)}</p>
      ) : (
        renderValue(content)
      )}
    </div>
  );
};

// Generic Section (fallback)
const GenericSection: React.FC<{ data: any; contentKey: string } & SectionEditProps> = ({ data, contentKey, isEditMode, editedContent, onContentChange }) => {
  const content = data?.[contentKey];
  if (!content) return null;

  // Handle various data shapes
  if (typeof content === 'string') {
    return <EditableText contentPath={`generic.${contentKey}`} originalValue={content} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} as="div" className="text-gray-700" />;
  }

  if (Array.isArray(content)) {
    return (
      <div className="generic-array-content">
        {content.map((item: any, index: number) => (
          <div key={index} className="mb-3 p-3 bg-gray-50 rounded">
            {typeof item === 'string' ? (
              <EditableText contentPath={`generic.${contentKey}.${index}`} originalValue={item} editedContent={editedContent} onContentChange={onContentChange} isEditMode={isEditMode} />
            ) : JSON.stringify(item, null, 2)}
          </div>
        ))}
      </div>
    );
  }

  if (typeof content === 'object') {
    return (
      <div className="generic-object-content">
        <pre className="text-sm bg-gray-50 p-3 rounded overflow-x-auto">
          {JSON.stringify(content, null, 2)}
        </pre>
      </div>
    );
  }

  return null;
};

// Main Section Renderer
export const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  data,
  contentWidth,
  isEditMode,
  editedContent,
  onContentChange,
  removedBlocks,
  onDiscoverBlocks
}) => {
  const editProps = { isEditMode, editedContent, onContentChange };

  const renderContent = () => {
    if (isSuiteSectionType(section.type)) {
      return <SuiteSection section={section} data={data} {...editProps} />;
    }
    switch (section.type) {
      case 'executive-summary':
        return <ExecutiveSummarySection data={data} {...editProps} />;

      case 'analysis-matrix':
        const matrix = data?.analysisMatrices?.[section.matrixIndex ?? 0];
        return <AnalysisMatrixSection matrix={matrix} title={section.title} matrixIndex={section.matrixIndex ?? 0} contentWidth={contentWidth} {...editProps} />;

      case 'key-findings':
        return <KeyFindingsSection data={data} {...editProps} />;

      case 'recommendations':
        return <RecommendationsSection data={data} {...editProps} />;

      // Per-item splits — the V2 paginator splits between items instead of
      // mid-card so a single recommendation never gets cut in half across a
      // page break. The 'recommendations' case above is kept for legacy
      // workflows that don't yet emit per-item sections.
      case 'recommendations-header':
        return null;
      case 'recommendation-item':
        return <SingleRecommendationSection data={data} index={section.matrixIndex ?? 0} {...editProps} />;

      case 'strategic-considerations':
        return <StrategicConsiderationsSection data={data} {...editProps} />;

      case 'risk-mitigation-strategies':
        return <RiskMitigationSection data={data} {...editProps} />;

      // === Document Comparison / Policy Comparison Section Types ===
      case 'non-standard-provisions':
        return <NonStandardProvisionsSection data={data} {...editProps} />;

      case 'clause-wording-comparison':
        return <ClauseWordingComparisonSection data={data} {...editProps} />;

      case 'coverage-analysis-matrices':
        return <CoverageAnalysisMatricesSection data={data} {...editProps} />;

      case 'risk-shift-navigator':
        return <RiskShiftNavigatorSection data={data} {...editProps} />;

      // === Loss Run Analyzer Section Types ===
      case 'document-manifest':
        return <DocumentManifestSection data={data} {...editProps} />;

      case 'claims-table':
        return <ClaimsTableSection data={data} {...editProps} />;

      case 'validation-results':
        return <ValidationResultsSection data={data} {...editProps} />;

      case 'claim-development':
        return <ClaimDevelopmentSection data={data} {...editProps} />;

      case 'frequency-analysis':
        return <FrequencyAnalysisSection data={data} {...editProps} />;

      case 'severity-analysis':
        return <SeverityAnalysisSection data={data} {...editProps} />;

      case 'top-claims':
        return <TopClaimsSection data={data} {...editProps} />;

      case 'data-quality':
        return <DataQualitySection data={data} {...editProps} />;

      case 'strategic-action-plan':
        return <StrategicActionPlanSection data={data} {...editProps} />;

      case 'confidence-statement':
        return <ConfidenceStatementSection data={data} {...editProps} />;

      case 'disclaimer':
        return <DisclaimerSection data={data} {...editProps} />;

      // === YOY Renewal opt-in add-on sections (Report Options step) ===
      // Header anchors render just a section title so the TOC entry has a
      // matching DOM element. The actual content lives in the per-item /
      // per-block sections that follow.
      case 'schedules-header':
      case 'coverage-details-header':
      case 'compliance-contractual-header':
        return null;
      case 'schedule-item':
        return <SingleScheduleSection data={data} scheduleIndex={section.matrixIndex ?? 0} contentWidth={contentWidth} />;
      case 'coverage-details-block':
        return <CoverageDetailsBlockSection data={data} blockKey={section.contentKey || ''} contentWidth={contentWidth} />;
      case 'compliance-block':
        return <ComplianceBlockSection data={data} blockKey={section.contentKey || ''} contentWidth={contentWidth} />;
      // Legacy single-section types — kept for older reports / fallback paths.
      // New reports use the split per-item / per-block sections above.
      case 'schedules-section':
        return <SchedulesSection data={data} contentWidth={contentWidth} />;
      case 'coverage-details-section':
        return <CoverageDetailsSection data={data} contentWidth={contentWidth} />;
      case 'compliance-contractual-section':
        return <ComplianceContractualSection data={data} contentWidth={contentWidth} />;

      case 'research-overview':
        return <ResearchOverviewSection data={data} {...editProps} />;

      case 'data-quality-assessment':
        return <DataQualityAssessmentSection data={data} {...editProps} />;

      // === Insurance Requirements v2.0 Section Types ===
      case 'insurance-executive-summary':
        return <InsuranceExecutiveSummarySection data={data} {...editProps} />;

      // === Client Renewal Presentation Section Types ===
      case 'client-renewal-executive-summary':
        return <ClientRenewalExecutiveSummarySection data={data} {...editProps} />;

      case 'compliance-matrix':
        return <ComplianceMatrixSection data={data} {...editProps} />;

      case 'interconnected-analysis':
        return <InterconnectedAnalysisSection data={data} {...editProps} />;

      case 'decision-pathway':
        return <DecisionPathwaySection data={data} {...editProps} />;

      case 'blind-spot-acknowledgement':
        return <BlindSpotAcknowledgementSection data={data} {...editProps} />;

      case 'confidence-assessment':
        return <ConfidenceAssessmentSection data={data} {...editProps} />;

      case 'professional-disclaimer':
        return <ProfessionalDisclaimerSection data={data} {...editProps} />;

      // === Commercial Lines Executive Renewal Section Types ===
      case 'executive-renewal-summary':
        return <ExecutiveRenewalSummarySection data={data} {...editProps} />;

      case 'renewal-program-matrix':
        return <RenewalProgramMatrixSection data={data} {...editProps} />;

      case 'entity-overview':
        return <EntityOverviewSection data={data} {...editProps} />;

      case 'research-methodology':
        return <ResearchMethodologySection data={data} {...editProps} />;

      case 'critical-path-priorities':
        return <CriticalPathPrioritiesSection data={data} {...editProps} />;

      case 'comprehensive-risk-assessment':
        return <ComprehensiveRiskAssessmentSection data={data} {...editProps} />;

      case 'coverage-applicability-analysis':
        return <CoverageApplicabilityAnalysisSection data={data} {...editProps} />;

      case 'risk-mitigation-framework':
        return <RiskMitigationFrameworkSection data={data} {...editProps} />;

      case 'risk-correlation-map':
        return <RiskCorrelationMapSection data={data} {...editProps} />;

      case 'confidence-and-limitations':
        return <ConfidenceAndLimitationsSection data={data} {...editProps} />;

      // === Financial Analysis Section Types ===
      case 'financial-risk-assessment':
        return <FinancialRiskAssessmentSection data={data} {...editProps} />;

      case 'detailed-financial-analysis':
        return <DetailedFinancialAnalysisSection data={data} {...editProps} />;

      case 'period-comparison':
        return <PeriodComparisonSection data={data} {...editProps} />;

      case 'erm-risk-mapping':
        return <ErmRiskMappingSection data={data} {...editProps} />;

      case 'underwriting-recommendation':
        return <UnderwritingRecommendationSection data={data} {...editProps} />;

      // === Client Renewal Presentation Section Types ===
      case 'company-context':
        return <CompanyContextSection data={data} {...editProps} />;

      case 'document-inventory':
        return <DocumentInventorySection data={data} {...editProps} />;

      case 'program-overview':
        return <ProgramOverviewSection data={data} {...editProps} />;

      case 'coverage-highlights':
        return <CoverageHighlightsSection data={data} {...editProps} />;

      case 'restrictive-endorsements':
        return <RestrictiveEndorsementsSection data={data} {...editProps} />;

      case 'premium-analysis':
        return <PremiumAnalysisSection data={data} {...editProps} />;

      case 'coverage-changes':
        return <CoverageChangesSection data={data} {...editProps} />;

      case 'red-flags':
        return <RedFlagsSection data={data} {...editProps} />;

      case 'positive-signals':
        return <PositiveSignalsSection data={data} {...editProps} />;

      case 'action-items':
        return <ActionItemsSection data={data} {...editProps} />;

      case 'conversation-points':
        return <ConversationPointsSection data={data} {...editProps} />;

      case 'glossary':
        return <GlossarySection data={data} {...editProps} />;

      // === Coverage Analysis Section Types ===
      case 'policy-overview':
        return <PolicyOverviewSection data={data} {...editProps} />;

      case 'coverage-recommendations':
        return <CoverageRecommendationsSection data={data} {...editProps} />;

      case 'coverage-gaps-opportunities':
        return <CoverageGapsOpportunitiesSection data={data} {...editProps} />;

      case 'scenario-testing':
        return <ScenarioTestingSection data={data} {...editProps} />;

      case 'coverage-metrics':
        return <CoverageMetricsSection data={data} {...editProps} />;

      case 'meta-analysis':
        return <MetaAnalysisSection data={data} {...editProps} />;

      // === Portfolio Analyzer Section Types ===
      case 'portfolio-executive-summary':
        return <PortfolioExecutiveSummarySection data={data} {...editProps} />;

      case 'portfolio-structure':
        return <PortfolioStructureSection data={data} {...editProps} />;

      case 'scenario-analysis':
        return <ScenarioAnalysisSection data={data} {...editProps} />;

      case 'portfolio-coverage-analysis':
        return <PortfolioCoverageAnalysisSection data={data} {...editProps} />;

      case 'portfolio-synthesis':
        return <PortfolioSynthesisSection data={data} {...editProps} />;

      case 'portfolio-assessment':
        return <PortfolioAssessmentSection data={data} {...editProps} />;

      case 'blind-spots':
        return <BlindSpotsSection data={data} {...editProps} />;

      // === Rate Analysis Section Types ===
      case 'rate-analysis-executive-summary':
        return <RateAnalysisExecutiveSummarySection data={data} {...editProps} />;

      case 'rate-analysis-tables':
        return <RateAnalysisTablesSection data={data} {...editProps} />;

      case 'rate-driver-hierarchy':
        return <RateDriverHierarchySection data={data} {...editProps} />;

      case 'rate-weak-signals':
        return <RateWeakSignalsSection data={data} {...editProps} />;

      case 'rate-trend-analysis':
        return <RateTrendAnalysisSection data={data} {...editProps} />;

      case 'rate-key-findings':
        return <RateKeyFindingsSection data={data} {...editProps} />;

      // === Personal Umbrella / Homeowners Comparison ===
      case 'umbrella-generic':
        return <UmbrellaGenericSection data={data} contentKey={section.contentKey || ''} />;

      default:
        // Try to render generic content based on contentKey
        if (section.contentKey) {
          return <GenericSection data={data} contentKey={section.contentKey} {...editProps} />;
        }
        return (
          <div className="text-gray-500 italic p-4 bg-gray-50 rounded">
            Section type "{section.type}" not yet implemented
          </div>
        );
    }
  };

  // Get edited title if available
  const displayTitle = editedContent?.get(`section.${section.id}.title`) ?? section.title;

  return (
    <div
      data-section-id={section.id}
      className="pdf-section"
      style={{ width: contentWidth }}
    >
      {isEditMode ? (
        <EditableText
          contentPath={`section.${section.id}.title`}
          originalValue={section.title}
          editedContent={editedContent}
          onContentChange={onContentChange}
          isEditMode={isEditMode}
          as="h4"
          className="text-xl font-bold mb-4 text-gray-800 border-b-2 border-gray-200 pb-2"
        />
      ) : (
        <h2 className="text-xl font-bold mb-4 text-gray-800 border-b-2 border-gray-200 pb-2">
          {displayTitle}
        </h2>
      )}
      <RemovableBlocksLayer
        sectionId={section.id}
        removedBlocks={removedBlocks}
        onDiscoverBlocks={onDiscoverBlocks}
      >
        {renderContent()}
      </RemovableBlocksLayer>
    </div>
  );
};

export default SectionRenderer;
